/* =========================================================================
   Ceramind Custom Rib Designer — v1.3  (2026-08-31)
   Theme asset. Upload to Online Store > Themes > Edit code > Assets.
   Pairs with: rib-designer-noshadow-block.liquid (v1.3)
   Built for the Concept theme (RoarTheme). No Shadow DOM — styles are
   scoped with a .ceramind-rib class prefix instead.

   v1.3  Clay shrinkage. A percentage and a Rib Size / Fired Size toggle let
         the customer work in the size they want the finished pot to be, and
         the tool works out the rib. Only the PROFILE is a pot dimension -
         the backing, the hole and the corner radius are the tool in your
         hand and never scale.
         NOTE: heights are no longer always multiples of five. A fired size
         produces a rib on any whole millimetre, so anything downstream that
         reads totalHeight must accept 153 as readily as 155.
   v1.2  Pot Preview tab, profile labels, folder tabs, iPad sizing fix.
   ========================================================================= */
(function(){

  // Every element this tool needs lives inside a Shadow DOM tree. Plain
  // ROOT.getElementById() can't see into a shadow tree from outside it,
  // so all lookups go through ROOT instead — the bootstrap script (in the
  // section/block that loads this file) sets window.__RIB_DESIGNER_ROOT__
  // to the shadow root before this script runs. Falls back to document so
  // this file still works if it's ever used outside a shadow tree.
  // No Shadow DOM in this build: styles are isolated via a .ceramind-rib class
  // prefix on every CSS rule instead. Element lookups therefore use the normal
  // document, scoped to the wrapper so IDs can't collide with theme elements.
  var SCOPE_EL = document.querySelector('.ceramind-rib');
  var ROOT = {
    getElementById: function(id){
      return SCOPE_EL ? SCOPE_EL.querySelector('#' + id) : document.getElementById(id);
    }
  };

  // Margins trimmed and matched. AXIS_BOTTOM moves with AXIS_TOP so CANVAS_H
  // stays exactly 380: saved sketches store bulges in pixels, so changing the
  // plot height would alter how an old sketch reloads.
  var AXIS_X = 42, AXIS_TOP = 34, AXIS_BOTTOM = 414, MIN_GAP_Y = 0, TIE_NUDGE = 0.05;
  var CANVAS_H = AXIS_BOTTOM - AXIS_TOP;
  var PLOT_RIGHT = AXIS_X + CANVAS_H/2;

  var state = {
    totalHeight: 110,
    // Top node must always sit at totalHeight; keep these in sync.
    nodes: [{y:0,x:11},{y:110,x:11}],   // a tenth of the height, as reset uses
    types: ['line'],
    bulges: [0],
    activeTool: 'edit',
    selectedBands: [],
    selectedNodes: [],
    curvatureArmedBand: null,
    // v1.1 rib view
    view: 'profile',          // 'profile' | 'rib'
    backingMm: 30,            // width of the flat backing beyond the widest
                              // point of the profile: 30mm min, 60mm max
    hole: null                // null, or {x, y, d} in mm (x,y = centre,
                              // measured with y=0 at the TOP of the rib)
  };

  var BACK_MIN = 30, BACK_MAX = 70;
  var HOLE_DEFAULT_D = 14, HOLE_MIN_D = 10, HOLE_MAX_D = 25;
  var HOLE_MARGIN = 3;      // clearance from every edge, in mm
  var CORNER_R = 3;         // radius on the two backing-side corners, in mm

  // A finger is far less precise than a cursor. Points are drawn small so the
  // profile stays readable, so give them a larger INVISIBLE hit area on touch
  // devices. Nothing changes visually; only what counts as a hit.
  function isCoarsePointer(){
    try { return window.matchMedia && window.matchMedia('(pointer: coarse)').matches; }
    catch(e){ return false; }
  }
  var NODE_HIT_R = isCoarsePointer() ? 17 : 9;
  var holeLock = 'none';    // 'none' | 'x' | 'y'  (which axis is frozen)
  // Last hole the customer positioned. Removing a hole should not throw away
  // the work of placing it - toggling it back on restores where it was.
  var lastHole = null;
  // Shown once per visit to the profile view: the first edit made while a
  // hole exists warns that the hole may need repositioning. Resets each time
  // they come back, so there is always exactly one warning per visit.
  var profileEditWarned = false;

  var dragState = null;
  var longPressTimer = null;
  var lastClick = {kind:null, index:null, time:0};
  var svg = ROOT.getElementById('canvas');
  var history = [];
  var HISTORY_LIMIT = 20;
  var hasShownReshapeHint = false;

  // --- Shopify integration: height -> price tier -> variant ---
  // Each size tier offers a specific set of heights. The Shopify variant
  // picker is the source of truth for which tier is active; the height
  // dropdown is filtered to that tier's heights.
  var TIER_HEIGHTS = [
    {tier:'Micro',       heights:[30, 35, 40, 45, 50, 55, 60]},
    {tier:'Small',       heights:[65, 70, 75, 80, 85, 90, 95]},
    {tier:'Medium',      heights:[100, 105, 110, 115, 120, 125, 130]},
    {tier:'Large',       heights:[135, 140, 145, 150, 155, 160, 165]},
    {tier:'Extra Large', heights:[170, 175, 180, 185, 190, 195, 200]}
  ];
  var MIN_H = 30, MAX_H = 200, H_STEP = 5;
  var activeTier = null;

  // ---------- Shrinkage ----------
  // Clay shrinks as it dries and fires, so a rib drawn from a finished pot's
  // measurements makes a pot that comes out too small. These let the customer
  // work in either the rib's own size or the size they want to end up with.
  //
  // Only the PROFILE is a pot dimension. The backing, the hole and the corner
  // radius are the tool in your hand and never scale - a finger does not
  // shrink. The design stored and sent is always the actual rib.
  var MAX_SHRINK = 20;          // beyond about 20% is not a real clay body
  var shrinkPct = 0;            // 0 means the feature is off
  var sizeMode = 'rib';         // 'rib' or 'fired'
  var backingNoted = false;     // cleared whenever the shrinkage changes

  function shrinkK(){ return 1 - (shrinkPct/100); }
  function firedMode(){ return sizeMode === 'fired' && shrinkPct > 0; }
  function toFired(mm){ return mm * shrinkK(); }
  function ribHeightFor(firedH){ return Math.round(firedH / shrinkK()); }

  function tierHeights(tier){
    for(var i=0;i<TIER_HEIGHTS.length;i++){
      if(TIER_HEIGHTS[i].tier === tier){ return TIER_HEIGHTS[i].heights; }
    }
    return null;
  }
  // By RANGE, not by exact membership. Working in fired sizes produces ribs on
  // arbitrary millimetres - 114mm, 193mm - which appear in no tier's list, so
  // an indexOf lookup would find no tier at all and the variant would never
  // switch.
  function heightToTierName(h){
    for(var i=0;i<TIER_HEIGHTS.length;i++){
      var hs = TIER_HEIGHTS[i].heights;
      if(h <= hs[hs.length-1] + 0.001){ return TIER_HEIGHTS[i].tier; }
    }
    return TIER_HEIGHTS[TIER_HEIGHTS.length-1].tier;
  }

  // Drive the theme's own variant picker so price, availability and the
  // selected option all update through the theme's normal code path.
  // Themes render the picker as radios, a select, or neither, so try each.
  function selectVariantTier(tier, attempt){
    attempt = attempt || 0;
    var sel = sizeSelectEl();

    // The picker may not exist yet when this first runs (Concept upgrades its
    // custom elements after our script). Retry briefly rather than silently
    // giving up, which previously left the variant on the old size.
    if(!sel){
      if(attempt < 20){
        setTimeout(function(){ selectVariantTier(tier, attempt+1); }, 100);
      }
      return false;
    }

    var idx = -1;
    for(var o=0;o<sel.options.length;o++){
      var val = (sel.options[o].value||'').trim();
      var txt = (sel.options[o].textContent||'').trim();
      if(val === tier || txt === tier){ idx = o; break; }
    }
    if(idx === -1){ return false; }
    if(sel.selectedIndex === idx){ return true; }

    // Set through the native property descriptor: custom-select components
    // often define their own `value` setter, which can swallow a plain
    // assignment without updating the underlying option state.
    try {
      var proto = window.HTMLSelectElement && window.HTMLSelectElement.prototype;
      var desc = proto && Object.getOwnPropertyDescriptor(proto, 'value');
      if(desc && desc.set){ desc.set.call(sel, sel.options[idx].value); }
      else { sel.value = sel.options[idx].value; }
    } catch(e){ sel.value = sel.options[idx].value; }

    sel.selectedIndex = idx;
    for(var k=0;k<sel.options.length;k++){
      if(k === idx){ sel.options[k].setAttribute('selected',''); }
      else { sel.options[k].removeAttribute('selected'); }
    }

    var fire = function(el){
      ['input','change'].forEach(function(t){
        try { el.dispatchEvent(new Event(t, {bubbles:true})); } catch(e){}
      });
    };
    fire(sel);
    var host = sel.closest ? sel.closest('custom-select, variant-selects, variant-radios, .product-form, [is]') : null;
    if(host && host !== sel){ fire(host); }

    // Verify it actually took; if the component reverted it, fall back to
    // setting the cart variant id directly so the order is still correct.
    if(sel.selectedIndex !== idx && attempt < 3){
      setTimeout(function(){ selectVariantTier(tier, attempt+1); }, 120);
    }

    var list = window.RIB_VARIANTS || [];
    for(var v=0; v<list.length; v++){
      if(list[v].tier === tier){
        var f = productForm();
        var idInput = f ? f.querySelector('input[name="id"], select[name="id"]') : document.querySelector('input[name="id"]');
        if(idInput && String(idInput.value) !== String(list[v].id)){
          idInput.value = list[v].id;
          fire(idInput);
        }
        break;
      }
    }
    return true;
  }

  function productForm(){
    return document.querySelector('form[action*="/cart/add"]');
  }
  function selectedVariantId(){
    var f = productForm();
    if(!f) return null;
    var input = f.querySelector('input[name="id"], select[name="id"]');
    return input ? String(input.value) : null;
  }
  function variantById(id){
    var list = window.RIB_VARIANTS || [];
    for(var i=0;i<list.length;i++){
      if(String(list[i].id) === String(id)){ return list[i]; }
    }
    return null;
  }
  function currentVariant(){
    var v = variantById(selectedVariantId());
    if(v) return v;
    var list = window.RIB_VARIANTS || [];
    if(activeTier){
      for(var i=0;i<list.length;i++){ if(list[i].tier === activeTier){ return list[i]; } }
    }
    return list[0] || null;
  }
  function updatePriceDisplay(){
    var el = ROOT.getElementById('priceDisplay');
    if(!el) return;
    var v = currentVariant();
    el.textContent = v ? v.price : '';
  }

  // Rebuild the height dropdown to only the heights valid for the given tier.
  // Keeps the current height if it's still valid, otherwise snaps to the
  // closest available one and rescales the design to match.
  function applyTier(tier, isInitial, preserveHeight){
    var heights = tierHeights(tier);
    if(!heights){ return; }
    activeTier = tier;
    var sel = ROOT.getElementById('heightInput');
    if(!sel) return;

    var keep = state.totalHeight;

    // In fired mode the list is the pot sizes this tier's ribs can produce.
    // Each option carries the rib height it needs, rounded to the nearest
    // millimetre, and anything needing a rib outside 30-200 is left out.
    var options = [];
    if(firedMode()){
      // Clean 5mm FIRED sizes, with whatever rib each one needs. Deriving the
      // fired sizes from the rib heights instead produced a list like 57, 62,
      // 66 - useless to someone who wants a 70mm mug. Only sizes whose rib
      // lands inside this tier are listed, so the picker still matches the
      // variant the customer has chosen.
      var lo = heights[0], hi = heights[heights.length-1];
      for(var fh = MIN_H; fh <= MAX_H; fh += H_STEP){
        var rib = ribHeightFor(fh);
        if(rib < lo || rib > hi) continue;
        if(rib < MIN_H || rib > MAX_H) continue;
        options.push({label: fh + ' mm', value: fh, rib: rib});
      }
    } else {
      heights.forEach(function(h){ options.push({label:h+' mm', value:h, rib:h}); });
    }
    // Never leave the picker empty.
    if(!options.length){
      heights.forEach(function(h){ options.push({label:h+' mm', value:h, rib:h}); });
    }

    // A design can arrive on a height that is not one of the listed steps -
    // an uploaded sketch drawn in fired sizes, for instance. Give it its own
    // entry rather than selecting the nearest, which would show a number the
    // design does not have.
    if(preserveHeight){
      var have = options.some(function(o){ return Math.abs(o.rib - keep) < 0.01; });
      if(!have && keep >= MIN_H && keep <= MAX_H){
        var label = firedMode() ? (Math.round(toFired(keep)) + ' mm') : (Math.round(keep) + ' mm');
        options.push({label: label, value: Math.round(firedMode() ? toFired(keep) : keep), rib: keep});
        options.sort(function(a,b){ return a.rib - b.rib; });
      }
    }

    var target = options.reduce(function(best,o){
      return Math.abs(o.rib-keep) < Math.abs(best.rib-keep) ? o : best;
    }, options[0]);

    sel.innerHTML = '';
    options.forEach(function(o){
      var opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      opt.setAttribute('data-rib', o.rib);
      if(o === target){ opt.selected = true; }
      sel.appendChild(opt);
    });
    target = target.rib;

    // An uploaded design carries its own height, which may not be one of the
    // listed steps - a fired-size rib can be any millimetre. Rebuilding the
    // picker must not resize it back onto a step, or the file reopens as a
    // different tool from the one that was saved.
    if(!preserveHeight && target !== state.totalHeight){
      var factor = target/state.totalHeight;
      state.nodes.forEach(function(n){ n.y = n.y*factor; n.x = n.x*factor; });
      scaleHole(factor);
      state.totalHeight = target;
    }
    // Guard: the profile must always span exactly 0..totalHeight, so pin the
    // endpoints rather than trusting accumulated float scaling.
    if(state.nodes.length >= 2){
      state.nodes[0].y = 0;
      state.nodes[state.nodes.length-1].y = state.totalHeight;
    }
    updatePriceDisplay();
    if(!isInitial){ render(); }
  }

  // Watch Shopify's variant picker. Theme variant JS varies, so this covers
  // the common paths: form change events, the hidden id input mutating, and
  // a low-frequency poll as a backstop.
  // Read the tier straight off the size picker. More reliable than the hidden
  // id input, which some themes update asynchronously or keep outside the form.
  function sizeSelectEl(){
    var candidates = document.querySelectorAll('select[name^="options"], select[name*="option"], select.select, variant-selects select, .product-form select');
    for(var i=0;i<candidates.length;i++){
      var s = candidates[i];
      if(s.name === 'id') continue;
      for(var o=0;o<s.options.length;o++){
        if(tierHeights((s.options[o].value||'').trim())){ return s; }
      }
    }
    return null;
  }
  function selectedTierFromPicker(){
    var sel = sizeSelectEl();
    if(sel && sel.value){
      var v = String(sel.value).trim();
      if(tierHeights(v)){ return v; }
    }
    var radios = document.querySelectorAll('input[type="radio"][name*="option"]:checked');
    for(var i=0;i<radios.length;i++){
      var rv = String(radios[i].value||'').trim();
      if(tierHeights(rv)){ return rv; }
    }
    var v2 = variantById(selectedVariantId());
    return v2 ? v2.tier : null;
  }

  function watchVariantPicker(){
    var lastTier = selectedTierFromPicker();
    function sync(){
      var tier = selectedTierFromPicker();
      if(!tier || tier === lastTier) return;
      lastTier = tier;
      // If the design's height already belongs to this tier, the picker is
      // only catching up with a change the tool made - uploading a file, or
      // choosing a fired size. Rebuilding the list must not resize the design
      // onto the nearest step, or a 153mm rib silently becomes 155mm.
      var already = heightToTierName(state.totalHeight) === tier;
      applyTier(tier, false, already);
    }
    var f = productForm();
    if(f){
      f.addEventListener('change', function(){ setTimeout(sync, 50); });
      var idInput = f.querySelector('input[name="id"]');
      if(idInput && window.MutationObserver){
        new MutationObserver(sync).observe(idInput, {attributes:true, attributeFilter:['value']});
      }
    }
    var sizeSel = sizeSelectEl();
    if(sizeSel){
      sizeSel.addEventListener('change', function(){ setTimeout(sync, 50); });
      if(window.MutationObserver){
        new MutationObserver(sync).observe(sizeSel, {attributes:true, attributeFilter:['value','selected']});
      }
    }
    document.addEventListener('change', function(){ setTimeout(sync, 50); });
    setInterval(sync, 400);
  }
  var locked = false;
  var addedToCart = false;

  function isDesignBlank(){
    return state.nodes.length===2 &&
      Math.abs(state.nodes[0].x)<0.01 && Math.abs(state.nodes[1].x)<0.01 &&
      state.types.length===1 && state.types[0]==='line' &&
      Math.abs(state.bulges[0])<0.01;
  }

  function snapshotState(){
    return {
      totalHeight: state.totalHeight,
      nodes: state.nodes.map(function(n){ return {x:n.x, y:n.y}; }),
      types: state.types.slice(),
      bulges: state.bulges.slice(),
      backingMm: state.backingMm,
      hole: state.hole ? {x:state.hole.x, y:state.hole.y, d:state.hole.d} : null
    };
  }

  function pushHistory(){
    if(state.view === 'profile' && state.hole && !profileEditWarned){
      profileEditWarned = true;
      setTimeout(showHoleReviewWarning, 0);
    }
    history.push(snapshotState());
    if(history.length > HISTORY_LIMIT){ history.shift(); }
  }

  function undo(){
    if(history.length === 0) return;
    var prev = history.pop();
    state.totalHeight = prev.totalHeight;
    state.nodes = prev.nodes;
    state.types = prev.types;
    state.bulges = prev.bulges;
    if(typeof prev.backingMm === 'number'){ state.backingMm = prev.backingMm; }
    state.hole = prev.hole ? {x:prev.hole.x, y:prev.hole.y, d:prev.hole.d} : null;
    clearSelection();
    render();
  }

  // Capture-phase handler: in sketch mode, a click should ALWAYS start a stroke,
  // even if it happens to land on an existing point or segment's hit-area
  // (those elements have generous hit zones for easier manual editing, which
  // would otherwise silently swallow the pointerdown and never start the sketch).
  svg.addEventListener('pointerdown', function(evt){
    if(locked) return;
    if(isSketchMode()){
      evt.stopPropagation();
      dragState = {kind:'sketch', raw:[getSvgPoint(evt)]};
      renderCanvas();
    }
  }, true);

  function isDoubleClick(kind, index){
    var now = Date.now();
    var hit = (lastClick.kind===kind && lastClick.index===index && (now-lastClick.time)<400);
    if(hit){
      lastClick = {kind:null, index:null, time:0};
    } else {
      lastClick = {kind:kind, index:index, time:now};
    }
    return hit;
  }

  function clamp(v,min,max){ return Math.max(min,Math.min(max,v)); }
  function scale(){ return CANVAS_H/state.totalHeight; }
  function xRange(){ return state.totalHeight/2; }
  function maxBulgePx(){ return xRange()*scale(); }
  function nodeYToSvgY(y){ return AXIS_BOTTOM - y*scale(); }
  function svgYToNodeY(sy){ return (AXIS_BOTTOM - sy)/scale(); }
  // Roughly where the profile sits at a given height. Linear between nodes is
  // close enough for deciding whether a label would be crossed.
  function profileXAtHeight(y){
    var n = state.nodes;
    if(!n.length) return 0;
    for(var i=0; i<n.length-1; i++){
      var a=n[i], b=n[i+1];
      if(y >= a.y && y <= b.y){
        var span = (b.y - a.y) || 1;
        return a.x + (b.x - a.x) * ((y - a.y)/span);
      }
    }
    return n[n.length-1].x;
  }

  function nodeXToSvgX(x){ return AXIS_X + x*scale(); }
  function svgXToNodeX(sx){ return (sx-AXIS_X)/scale(); }

  function clearSelection(){
    state.selectedBands = [];
    state.selectedNodes = [];
    state.curvatureArmedBand = null;
  }

  function isSketchMode(){
    return state.activeTool==='sketch-smooth' || state.activeTool==='sketch-line';
  }

  function safeYClamp(idx, desiredY){
    var prevY = state.nodes[idx-1].y;
    var nextY = state.nodes[idx+1].y;
    var lo = prevY, hi = nextY;
    if(idx-2>=0 && state.nodes[idx-2].y === prevY){ lo = prevY + TIE_NUDGE; }
    if(idx+2<state.nodes.length && state.nodes[idx+2].y === nextY){ hi = nextY - TIE_NUDGE; }
    if(lo>hi){ lo=hi; }
    return clamp(desiredY, lo, hi);
  }

  function chooseStep(H){
    var target = 8;
    var raw = H/target;
    var steps = [1,2,5,10,20,25,50,100];
    var best = steps[0];
    for(var k=0;k<steps.length;k++){
      if(Math.abs(steps[k]-raw) < Math.abs(best-raw)) best = steps[k];
    }
    return best;
  }

  function getSvgPoint(evt){
    var rect = svg.getBoundingClientRect();
    var vb = svg.viewBox.baseVal;
    var scaleX = vb.width / rect.width;
    var scaleY = vb.height / rect.height;
    return {
      x: (evt.clientX - rect.left) * scaleX + vb.x,
      y: (evt.clientY - rect.top) * scaleY + vb.y
    };
  }

  function bandGeometry(i){
    var a = state.nodes[i], b = state.nodes[i+1];
    var ax = nodeXToSvgX(a.x), ay = nodeYToSvgY(a.y);
    var bx = nodeXToSvgX(b.x), by = nodeYToSvgY(b.y);
    var dx = bx-ax, dy = by-ay;
    var len = Math.sqrt(dx*dx+dy*dy) || 1;
    var px = -dy/len, py = dx/len;
    return {ax:ax,ay:ay,bx:bx,by:by,dx:dx,dy:dy,px:px,py:py,len:len};
  }

  function svgNode(i){
    var n = state.nodes[i];
    return {x: nodeXToSvgX(n.x), y: nodeYToSvgY(n.y)};
  }

  function unitDir(p1,p2){
    var dx = p2.x-p1.x, dy = p2.y-p1.y;
    var len = Math.sqrt(dx*dx+dy*dy) || 1;
    return {x:dx/len, y:dy/len};
  }

  function isCurveAt(i){
    return i>=0 && i<state.types.length && state.types[i]==='curve';
  }

  function adjacentBandsForNodes(nodeIndices){
    var set = {};
    nodeIndices.forEach(function(idx){
      if(idx-1>=0 && idx-1<state.types.length){ set[idx-1]=true; }
      if(idx>=0 && idx<state.types.length){ set[idx]=true; }
    });
    return Object.keys(set).map(function(s){ return parseInt(s,10); });
  }

  // ---- Curve control points -------------------------------------------
  // A node shared by two curves carries ONE tangent direction, and both
  // curves must place their handles along it. That makes a smooth joint
  // structural rather than incidental: it is not possible to render a kink
  // where two curves meet.
  //
  // Bulge therefore rotates that tangent rather than shoving the handles
  // sideways. Sideways offsets were the old behaviour and are what tore the
  // joint open, because each segment pushed the shared node a different way.
  //
  // Nodes next to a LINE keep a band-local tangent, so straight-into-curve
  // corners stay as sharp as they were drawn.
  function rotVec(v, a){
    var c = Math.cos(a), s = Math.sin(a);
    return {x: v.x*c - v.y*s, y: v.x*s + v.y*c};
  }

  // How far a bulge rotates the tangent away from its segment's chord.
  //
  // Approaches 90 degrees but never reaches it. Past 90 the tangent points
  // BACK out of its own segment, so the curve wants to double back in
  // height - and since a segment endpoint sits exactly on its own height
  // bound, the allowed handle collapses to nothing the instant it happens.
  // That is what makes a curve snap backwards mid-drag, go flat near a node,
  // and bow past the outer edge.
  //
  // Matches the previous mapping, 2*atan(1.5b/L), to first order, so gentle
  // curves are unchanged. Nothing is lost at the deep end either: past 90
  // degrees the curve folds back on itself and reaches LESS far, not more.
  function bulgeAngle(P, types, bulges, i){
    if(types[i] !== 'curve') return 0;
    var dx = P[i+1].x-P[i].x, dy = P[i+1].y-P[i].y;
    var L = Math.sqrt(dx*dx+dy*dy) || 1;
    return Math.atan2(3*(bulges[i]||0), L);
  }

  function chordDir(P, i){ return unitDir(P[i], P[i+1]); }

  // The single tangent at a node with curves on both sides: the average of
  // what each neighbour wants. Returns null when the node is not a
  // curve-to-curve joint, so the caller falls back to a band-local tangent.
  function sharedTangent(P, types, bulges, k){
    var hasPrev = (k-1 >= 0 && types[k-1] === 'curve');
    var hasNext = (k < types.length && types[k] === 'curve');
    if(!(hasPrev && hasNext)) return null;
    var a = rotVec(chordDir(P, k-1), -bulgeAngle(P, types, bulges, k-1));
    var b = rotVec(chordDir(P, k),    bulgeAngle(P, types, bulges, k));
    var sx = a.x+b.x, sy = a.y+b.y;
    var L = Math.sqrt(sx*sx+sy*sy);
    if(L < 1e-6) return b;
    // NOTE: an earlier version nudged this tangent toward vertical at
    // turning points, to stop heavily bulged curves overshooting their own
    // node. It was removed because any such correction has to switch on
    // somewhere, and a switch is a discontinuity: the curve visibly snapped
    // while a point was being dragged through it. The sideways bow limit in
    // controlPointsFor handles overshoot instead, and varies continuously.
    return {x: sx/L, y: sy/L};
  }

  // Keep a handle inside bounds by SHORTENING it along its own direction.
  // Clipping x and y separately used to rotate the handle, which changed the
  // tangent and produced the sharp bits.
  // How much of a handle fits inside the bounds. Shortens only, never
  // redirects. No tolerance is needed here: bulgeAngle keeps the tangent
  // under 90 degrees from its chord, so a handle never points back out of
  // its own segment and the height bound never collapses at an endpoint.
  function fitFactor(S, D, h, xMin, xMax, yMin, yMax){
    var f = 1;
    function lim(s, d, lo, hi){
      var m = d*h;
      if(m > 1e-9){ var t = (hi - s)/m; if(t < f) f = t; }
      else if(m < -1e-9){ var t2 = (lo - s)/m; if(t2 < f) f = t2; }
    }
    lim(S.x, D.x, xMin, xMax);
    lim(S.y, D.y, yMin, yMax);
    return Math.max(0, Math.min(1, f));
  }

  // Shared by every render path (editor canvas, rib view, SVG export) so the
  // shape can never differ between them.
  // Pick a usable handle. The shared tangent is preferred, but the profile
  // must stay a function of height, so a control point may not run past the
  // segment's own top or bottom. Where that would crush the handle to nothing
  // — most obviously on a horizontal segment, whose vertical span is zero —
  // fall back to the segment's own chord direction. A slight kink there is
  // unavoidable and correct; a collapsed handle would be a hard corner, which
  // is the very thing being fixed.
  // When a node sits exactly ON a bound — at the spine, or at maximum width,
  // both of which are common — a handle with any component pointing outward
  // gets a fit factor of zero and collapses, producing the hard corner we are
  // trying to remove. Slide the handle ALONG that boundary instead: drop the
  // offending component and renormalise, preserving handle length.

  // Only ever SHORTENS a handle - never redirects it, and never crushes it
  // to nothing. Both of those change the drawn tangent at a node, which is
  // a visible sharp point. A floor on the length keeps the direction
  // meaningful; the cost is a fraction of a millimetre of overshoot past a
  // bound, which is invisible, where a sharp point is not.
  // Smallest allowed handle, as a fraction of the full length. Only needed
  // where a bound would otherwise crush a handle to nothing, which makes the
  // drawn tangent meaningless and shows as a sharp point. Kept as small as
  // possible: any floor lets a control point sit fractionally past a bound.
  var MIN_HANDLE = 0.08;
  function pickHandle(S, D, fallbackD, handle, xMin, xMax, yLo, yHi){
    var f = fitFactor(S, D, handle, xMin, xMax, yLo, yHi);
    return {D: D, f: Math.max(f, MIN_HANDLE)};
  }

  function controlPointsFor(P, types, bulges, i, xMin, xMax){
    var A = P[i], B = P[i+1];
    var dx = B.x-A.x, dy = B.y-A.y;
    var segLen = Math.sqrt(dx*dx+dy*dy) || 1;
    var handle = segLen/3;
    var th = bulgeAngle(P, types, bulges, i);

    var localA = rotVec(chordDir(P, i),  th);
    var localB = rotVec(chordDir(P, i), -th);
    var tA = sharedTangent(P, types, bulges, i)   || localA;
    var tB = sharedTangent(P, types, bulges, i+1) || localB;

    var yLo = Math.min(A.y, B.y), yHi = Math.max(A.y, B.y);

    var hA = pickHandle(A, tA, localA, handle, xMin, xMax, yLo, yHi);
    var hB = pickHandle(B, {x:-tB.x,y:-tB.y}, {x:-localB.x,y:-localB.y},
                        handle, xMin, xMax, yLo, yHi);

    // Keep a segment from bowing further sideways than its own bulge asks
    // for. A shared tangent is set by the NEIGHBOURS, so a segment the
    // customer never bulged can still get dragged far past its own two
    // points - the curve bulges where nothing was drawn. Allow the segment's
    // own bulge, plus a little slack so gentle splines are untouched.
    //
    // Applied as a pure shortening after the direction is settled, so the
    // tangent, and therefore the smoothness of the joint, is unchanged.
    var allow = Math.max(Math.abs(bulges[i] || 0), 4);
    var loX = Math.min(A.x, B.x) - allow, hiX = Math.max(A.x, B.x) + allow;
    function limitSideways(S, h){
      if(h.f <= 0) return h;
      var reach = S.x + h.D.x*handle*h.f;
      var f = h.f;
      if(reach > hiX && h.D.x > 1e-9)      f = Math.min(f, (hiX - S.x)/(h.D.x*handle));
      else if(reach < loX && h.D.x < -1e-9) f = Math.min(f, (loX - S.x)/(h.D.x*handle));
      return {D: h.D, f: Math.max(0, f)};
    }
    hA = limitSideways(A, hA);
    hB = limitSideways(B, hB);

    return {
      A: A, B: B,
      C1: {x: A.x + hA.D.x*handle*hA.f, y: A.y + hA.D.y*handle*hA.f},
      C2: {x: B.x + hB.D.x*handle*hB.f, y: B.y + hB.D.y*handle*hB.f}
    };
  }

  function curveControlPoints(i){
    var P = [];
    for(var k=0;k<state.nodes.length;k++){ P.push(svgNode(k)); }
    // No upper x bound: a handle may reach past the plot edge without being
    // drawn there. Only the spine matters, so the curve can't cross it.
    // No x bounds on the handles at all. A control point is construction
    // geometry, never drawn. Bounding it forces one of two bad outcomes near
    // the spine: zeroing the outward component changes the handle DIRECTION
    // (a visible sharp point), or the fit factor collapses the handle to
    // nothing (also a sharp point). Letting it cross costs a fraction of a
    // millimetre of curve, and removes both.
    // Bound the outward side too, or a hard bend at the maximum width draws
    // the curve straight off the grid.
    return controlPointsFor(P, state.types, state.bulges, i, AXIS_X, AXIS_X + xRange()*scale());
  }

  function bandCommand(i){
    if(state.types[i] !== 'curve'){
      var B = svgNode(i+1);
      return 'L'+B.x+','+B.y;
    }
    var cp = curveControlPoints(i);
    return 'C'+cp.C1.x+','+cp.C1.y+' '+cp.C2.x+','+cp.C2.y+' '+cp.B.x+','+cp.B.y;
  }

  function pathForBand(i){
    var A = svgNode(i);
    return 'M'+A.x+','+A.y+' '+bandCommand(i);
  }

  function fullProfileD(){
    var A0 = svgNode(0);
    var d = 'M'+A0.x+','+A0.y;
    for(var i=0;i<state.types.length;i++){ d += ' ' + bandCommand(i); }
    return d;
  }

  function fillPathD(){
    var d = fullProfileD();
    var top = svgNode(state.nodes.length-1);
    var bottom = svgNode(0);
    d += ' L'+PLOT_RIGHT+','+top.y+' L'+PLOT_RIGHT+','+bottom.y+' Z';
    return d;
  }

  function findBandForY(y){
    for(var i=0;i<state.nodes.length-1;i++){
      if(y >= state.nodes[i].y && y <= state.nodes[i+1].y) return i;
    }
    return -1;
  }

  function bezierPoint(A,C1,C2,B,t){
    var mt = 1-t;
    return {
      x: mt*mt*mt*A.x + 3*mt*mt*t*C1.x + 3*mt*t*t*C2.x + t*t*t*B.x,
      y: mt*mt*mt*A.y + 3*mt*mt*t*C1.y + 3*mt*t*t*C2.y + t*t*t*B.y
    };
  }

  function findTForY(A,C1,C2,B,targetY){
    var lo=0, hi=1;
    var descending = B.y < A.y;
    for(var iter=0; iter<24; iter++){
      var mid = (lo+hi)/2;
      var py = bezierPoint(A,C1,C2,B,mid).y;
      if(descending){
        if(py > targetY){ lo = mid; } else { hi = mid; }
      } else {
        if(py < targetY){ lo = mid; } else { hi = mid; }
      }
    }
    return (lo+hi)/2;
  }

  function smoothPointJoint(nodeIdx){
    var prevBand = nodeIdx-1, nextBand = nodeIdx;
    if(!isCurveAt(prevBand) || !isCurveAt(nextBand)) return;
    pushHistory();
    var gp = bandGeometry(prevBand), gn = bandGeometry(nextBand);
    var ratioPrev = state.bulges[prevBand]/gp.len;
    var ratioNext = state.bulges[nextBand]/gn.len;
    var avgRatio = (ratioPrev+ratioNext)/2;
    state.bulges[prevBand] = clamp(avgRatio*gp.len, -maxBulgePx(), maxBulgePx());
    state.bulges[nextBand] = clamp(avgRatio*gn.len, -maxBulgePx(), maxBulgePx());
    render();
  }

  // ---------- v1.1 rib view ----------
  var ribSvg = null, ribDrag = null;

  function spacerCentre(geo){ return geo.maxX + geo.backing/2; }

  // The hole is anchored to the shape, so it has to follow every change to
  // that shape. Scaling handles height changes; clamping catches everything
  // else (profile edits, backing changes, imports). Called before the design
  // is serialised, so what reaches the cart is always manufacturable.
  // The hole moves with the rib but keeps its size: it is a feature of the
  // tool, and a finger does not shrink. Position scales so a hole placed near
  // the top stays near the top.
  function scaleHoleProfileOnly(factor){
    if(!state.hole) return;
    state.hole.x *= factor;
    state.hole.y *= factor;
    reconcileHole();
  }

  function scaleHole(factor){
    if(!state.hole) return;
    state.hole.x *= factor;
    state.hole.y *= factor;
  }

  function reconcileHole(){
    if(!state.hole) return false;
    var geo = ribGeometry();
    if(holeFits(state.hole.x, state.hole.y, state.hole.d, geo)) return false;

    var d = clamp(state.hole.d, HOLE_MIN_D, HOLE_MAX_D);
    var pos = clampHole(state.hole.x, state.hole.y, d, geo);
    if(pos){ state.hole.x = pos.x; state.hole.y = pos.y; state.hole.d = d; return true; }

    // Won't fit at this size anywhere: shrink to the largest that will.
    var best = largestHoleThatFits(spacerCentre(geo), geo.H/2, geo);
    if(best >= HOLE_MIN_D){
      var p2 = clampHole(spacerCentre(geo), geo.H/2, best, geo);
      if(p2){ state.hole = {x:p2.x, y:p2.y, d:best}; return true; }
    }
    // No room for a hole at all.
    state.hole = null;
    return true;
  }

  function defaultHolePosition(geo){
    var y = geo.H/2;
    var pos = clampHole(spacerCentre(geo), y, HOLE_DEFAULT_D, geo);
    return pos ? {x:pos.x, y:pos.y, d:HOLE_DEFAULT_D} : null;
  }

  function ribPointFromEvent(evt){
    if(!ribSvg || !ribSvg.getScreenCTM) return null;
    var ctm = ribSvg.getScreenCTM();
    if(!ctm) return null;
    var pt = ribSvg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    var loc = pt.matrixTransform(ctm.inverse());
    return {x: loc.x, y: loc.y};
  }

  function renderRibCanvas(){
    ribSvg = ROOT.getElementById('ribCanvas');
    if(!ribSvg) return;
    var geo = ribGeometry();
    var pad = 6;
    var vw = geo.backX + pad*2, vh = geo.H + pad*2;
    ribSvg.setAttribute('viewBox', (-pad)+' '+(-pad)+' '+vw+' '+vh);

    var parts = [];
    parts.push('<path d="'+geo.d+'" fill="rgba(164,136,110,0.18)" stroke="#A4886E" stroke-width="'+(geo.H/220)+'" stroke-linejoin="round"/>');

    // Where the backing strip begins: the widest point of the drawn profile.
    // Everything to the right of this line is the backing width.
    var gw = geo.H/500;
    parts.push('<line x1="'+geo.maxX+'" y1="0" x2="'+geo.maxX+'" y2="'+geo.H+'" stroke="#A4886E" stroke-width="'+(gw*1.4)+'" stroke-dasharray="'+(geo.H/220)+' '+(geo.H/150)+'" opacity="0.9"/>');

    // Centre of the flat backing strip, used by Snap to centre.
    var cx = spacerCentre(geo);
    parts.push('<line x1="'+cx+'" y1="0" x2="'+cx+'" y2="'+geo.H+'" stroke="#D2C1B4" stroke-width="'+gw+'" stroke-dasharray="'+(geo.H/60)+' '+(geo.H/60)+'"/>');

    if(state.hole){
      var r = state.hole.d/2;
      var hx = state.hole.x, hy = state.hole.y;
      parts.push('<circle id="holeRing" cx="'+hx+'" cy="'+hy+'" r="'+r+'" fill="#FFFFFF" stroke="#473728" stroke-width="'+(geo.H/220)+'"/>');
      parts.push('<line id="holeCrossH" x1="'+(hx-r*0.45)+'" y1="'+hy+'" x2="'+(hx+r*0.45)+'" y2="'+hy+'" stroke="#A4886E" stroke-width="'+(geo.H/400)+'"/>');
      parts.push('<line id="holeCrossV" x1="'+hx+'" y1="'+(hy-r*0.45)+'" x2="'+hx+'" y2="'+(hy+r*0.45)+'" stroke="#A4886E" stroke-width="'+(geo.H/400)+'"/>');
      // Hit area scaled for fingers as well as cursors.
      var hitR = Math.max(r, geo.H/(isCoarsePointer() ? 12 : 28));
      parts.push('<circle id="holeHit" class="holeHit" cx="'+hx+'" cy="'+hy+'" r="'+hitR+'" fill="transparent" style="cursor:grab;touch-action:none;"/>');
    }
    ribSvg.innerHTML = parts.join('');
    wireRibCanvas();
  }

  // Move the existing hole graphics rather than rebuilding the canvas.
  // Rebuilding mid-drag destroys the element holding pointer capture, which
  // silently ends the drag after the first movement.
  function updateHoleGraphics(){
    if(!state.hole || !ribSvg) return;
    var ring = ribSvg.querySelector('#holeRing');
    if(!ring) { renderRibCanvas(); return; }
    var r = state.hole.d/2, hx = state.hole.x, hy = state.hole.y;
    ring.setAttribute('cx', hx); ring.setAttribute('cy', hy); ring.setAttribute('r', r);
    var ch = ribSvg.querySelector('#holeCrossH');
    var cv = ribSvg.querySelector('#holeCrossV');
    var hit = ribSvg.querySelector('#holeHit');
    if(ch){ ch.setAttribute('x1', hx-r*0.45); ch.setAttribute('y1', hy); ch.setAttribute('x2', hx+r*0.45); ch.setAttribute('y2', hy); }
    if(cv){ cv.setAttribute('x1', hx); cv.setAttribute('y1', hy-r*0.45); cv.setAttribute('x2', hx); cv.setAttribute('y2', hy+r*0.45); }
    if(hit){
      hit.setAttribute('cx', hx); hit.setAttribute('cy', hy);
      hit.setAttribute('r', Math.max(r, state.totalHeight/(isCoarsePointer() ? 12 : 28)));
    }
  }

  // Delegated on the SVG itself so it survives canvas rebuilds.
  var ribCanvasWired = false;
  function wireRibCanvas(){
    if(ribCanvasWired || !ribSvg) return;
    ribCanvasWired = true;
    ribSvg.addEventListener('pointerdown', function(evt){
      if(locked || !state.hole) return;
      var t = evt.target;
      if(!t || t.id !== 'holeHit') return;
      evt.preventDefault();
      evt.stopPropagation();
      var p = ribPointFromEvent(evt);
      if(!p) return;
      ribDrag = {dx: state.hole.x - p.x, dy: state.hole.y - p.y, moved:false};
      try { ribSvg.setPointerCapture(evt.pointerId); } catch(e){}
    });
  }

  // Update the X/Y boxes in place, so dragging and typing stay in agreement
  // without rebuilding the panel mid-interaction.
  function syncHoleFields(){
    if(!state.hole) return;
    var hx = ROOT.getElementById('holeX'), hy = ROOT.getElementById('holeY');
    if(hx && document.activeElement !== hx){ hx.value = Math.round(state.hole.x*10)/10; }
    if(hy && document.activeElement !== hy){ hy.value = Math.round(state.hole.y*10)/10; }
  }

  function moveHoleTo(x, y){
    var geo = ribGeometry();
    if(holeLock === 'x'){ x = state.hole.x; }
    if(holeLock === 'y'){ y = state.hole.y; }
    var pos = clampHole(x, y, state.hole.d, geo);
    if(pos){
      state.hole.x = pos.x; state.hole.y = pos.y;
      lastHole = {x:state.hole.x, y:state.hole.y, d:state.hole.d};
    }
  }

  document.addEventListener('pointermove', function(evt){
    if(!ribDrag || !state.hole) return;
    var p = ribPointFromEvent(evt);
    if(!p) return;
    if(!ribDrag.moved){ ribDrag.moved = true; pushHistory(); }
    moveHoleTo(p.x + ribDrag.dx, p.y + ribDrag.dy);
    updateHoleGraphics();
    syncHoleFields();
  });
  document.addEventListener('pointerup', function(){
    if(ribDrag){ ribDrag = null; render(); }
  });

  // Backing width and the add/remove control sit above the canvas, since
  // they change the shape itself. Hole positioning stays below, next to the
  // drawing it refers to.
  function renderRibPanel(){
    var top = ROOT.getElementById('ribTopPanel');
    var el = ROOT.getElementById('ribPanel');
    if(!top || !el) return;

    var t = '<div class="rib-top-grid">';
    t += '<div class="rib-col"><div class="field-row"><label for="backingInput">Backing width</label>' +
         '<input type="number" id="backingInput" min="'+BACK_MIN+'" max="'+BACK_MAX+'" step="1" value="'+Math.round(state.backingMm)+'"'+(locked?' disabled':'')+'>' +
         '<span class="unit">mm</span></div>' +
         '<span class="rib-note backing-hint">'+BACK_MIN+'\u2013'+BACK_MAX+'mm, measured from the dotted line to the right edge</span></div>';
    if(!state.hole){
      t += '<div class="rib-col"><div class="field-row"><button type="button" id="addHoleBtn"'+(locked?' disabled':'')+'>Add a Hole</button></div>' +
           '<span class="rib-note">Optional \u2014 easier to grip, and handy for hanging it up in the studio</span></div>';
    } else {
      t += '<div class="rib-col"><div class="field-row"><button type="button" id="removeHoleBtn"'+(locked?' disabled':'')+'>Remove Hole</button></div>' +
           '<span class="rib-note">Position and size it below</span></div>';
    }
    t += '</div>';
    top.innerHTML = t;

    var h = '';
    if(state.hole){
      var geoNow = ribGeometry();
      h += '<div class="field-row"><label for="holeDia">Hole diameter</label>' +
           '<input type="number" id="holeDia" min="'+HOLE_MIN_D+'" max="'+HOLE_MAX_D+'" step="0.5" value="'+(Math.round(state.hole.d*2)/2)+'"'+(locked?' disabled':'')+'>' +
           '<span class="unit">mm</span><span class="rib-note">min '+HOLE_MIN_D+', max '+HOLE_MAX_D+'</span></div>';
      h += '<div class="field-row pos-row"><label for="holeX">X</label>' +
           '<input type="number" id="holeX" step="0.5" min="0" max="'+Math.round(geoNow.backX)+'" value="'+(Math.round(state.hole.x*10)/10)+'"'+(locked?' disabled':'')+'>' +
           '<span class="unit">mm</span>' +
           '<label for="holeY">Y</label>' +
           '<input type="number" id="holeY" step="0.5" min="0" max="'+Math.round(geoNow.H)+'" value="'+(Math.round(state.hole.y*10)/10)+'"'+(locked?' disabled':'')+'>' +
           '<span class="unit">mm</span>' +
           '<span class="rib-note">from top-left</span></div>';
      h += '<div class="field-row"><button type="button" id="snapCentreBtn"'+(locked?' disabled':'')+'>Snap to Centre</button>' +
           '<button type="button" id="lockXBtn" class="'+(holeLock==='x'?'active':'')+'"'+(locked?' disabled':'')+'>Lock X</button>' +
           '<button type="button" id="lockYBtn" class="'+(holeLock==='y'?'active':'')+'"'+(locked?' disabled':'')+'>Lock Y</button></div>';
      h += '<p class="hint">Drag the hole to position it. It stays at least '+HOLE_MARGIN+'mm inside every edge. Lock X to slide it up and down only; Lock Y to slide it across only.</p>';
    }
    el.innerHTML = h;
    // Nothing below the canvas until a hole exists, so drop the divider too.
    var div = ROOT.getElementById('ribDivider');
    if(div){ div.style.display = state.hole ? '' : 'none'; }
  }

  // Panel controls are wired by delegation on the container, which survives
  // innerHTML rebuilds. Binding directly to the buttons meant a pending
  // change event could rebuild the panel mid-click and swallow the first
  // press — e.g. typing a backing width then clicking "Add a hole".
  var ribPanelWired = false;
  function wireRibPanel(){
    if(ribPanelWired) return;
    var panel = ROOT.getElementById('ribView');
    if(!panel) return;
    ribPanelWired = true;

    panel.addEventListener('click', function(evt){
      if(locked) return;
      var t = evt.target;
      if(!t || !t.id) return;
      var geo = ribGeometry();

      if(t.id === 'addHoleBtn'){
        var pos = null;
        // Restore the previous hole if it still fits the current shape,
        // rather than starting from the default position again.
        if(lastHole){
          var d0 = clamp(lastHole.d, HOLE_MIN_D, HOLE_MAX_D);
          var back = clampHole(lastHole.x, lastHole.y, d0, geo);
          if(back) pos = {x:back.x, y:back.y, d:d0};
        }
        if(!pos) pos = defaultHolePosition(geo);
        if(!pos){
          showRibNote('There is not enough room for a hole. Try widening the backing.');
          return;
        }
        pushHistory();
        state.hole = pos;
        showRibNote('');
        render();
      } else if(t.id === 'removeHoleBtn'){
        pushHistory();
        if(state.hole){ lastHole = {x:state.hole.x, y:state.hole.y, d:state.hole.d}; }
        state.hole = null;
        showRibNote('');
        render();
      } else if(t.id === 'snapCentreBtn'){
        if(!state.hole) return;
        pushHistory();
        var p = clampHole(spacerCentre(geo), state.hole.y, state.hole.d, geo);
        if(p){ state.hole.x = p.x; state.hole.y = p.y; }
        render();
      } else if(t.id === 'lockXBtn'){
        holeLock = (holeLock === 'x') ? 'none' : 'x';
        renderRibPanel();
      } else if(t.id === 'lockYBtn'){
        holeLock = (holeLock === 'y') ? 'none' : 'y';
        renderRibPanel();
      }
    });

    panel.addEventListener('focusin', function(evt){
      if(locked) return;
      if(evt.target && ['backingInput','holeDia','holeX','holeY'].indexOf(evt.target.id) !== -1){
        pushHistory();
      }
    });

    // Live updates redraw the canvas only. The panel itself is rebuilt on
    // blur, so controls never vanish while the customer is still using them.
    panel.addEventListener('input', function(evt){
      if(locked) return;
      var t = evt.target;
      if(!t) return;
      if(t.id === 'backingInput'){
        var v = parseFloat(t.value);
        if(isNaN(v)) return;
        state.backingMm = clamp(v, BACK_MIN, BACK_MAX);
        backingNoted = true;      // they have dealt with it
        if(state.hole){
          var g = ribGeometry();
          var p = clampHole(state.hole.x, state.hole.y, state.hole.d, g);
          if(p){ state.hole.x = p.x; state.hole.y = p.y; }
        }
        renderRibCanvas();
        // The note lives outside the canvas, so redrawing the rib alone leaves
        // it on screen after they have acted on it.
        if(typeof refreshShrinkUI === 'function'){ refreshShrinkUI(); }
      } else if((t.id === 'holeX' || t.id === 'holeY') && state.hole){
        var val = parseFloat(t.value);
        if(isNaN(val)) return;
        var gp = ribGeometry();
        var tx = (t.id === 'holeX') ? val : state.hole.x;
        var ty = (t.id === 'holeY') ? val : state.hole.y;
        var pp = clampHole(tx, ty, state.hole.d, gp);
        if(pp){
          state.hole.x = pp.x; state.hole.y = pp.y;
          lastHole = {x:state.hole.x, y:state.hole.y, d:state.hole.d};
          updateHoleGraphics();
        }
      } else if(t.id === 'holeDia' && state.hole){
        var d = parseFloat(t.value);
        if(isNaN(d)) return;
        d = clamp(d, HOLE_MIN_D, HOLE_MAX_D);
        var g2 = ribGeometry();
        var p2 = clampHole(state.hole.x, state.hole.y, d, g2);
        if(!p2){
          var best = largestHoleThatFits(state.hole.x, state.hole.y, g2);
          if(best < HOLE_MIN_D) return;
          d = best;
          p2 = clampHole(state.hole.x, state.hole.y, d, g2);
        }
        state.hole.d = d;
        state.hole.x = p2.x; state.hole.y = p2.y;
        lastHole = {x:state.hole.x, y:state.hole.y, d:state.hole.d};
        renderRibCanvas();
      }
    });

    panel.addEventListener('change', function(evt){
      if(locked) return;
      var t = evt.target;
      if(!t) return;
      if(t.id === 'backingInput'){ t.value = Math.round(state.backingMm); }
      else if(t.id === 'holeDia' && state.hole){ t.value = Math.round(state.hole.d*2)/2; }
      else if(t.id === 'holeX' && state.hole){ t.value = Math.round(state.hole.x*10)/10; }
      else if(t.id === 'holeY' && state.hole){ t.value = Math.round(state.hole.y*10)/10; }
    });
  }

  // ---------- Pot preview ----------
  // An impression of the vessel this profile throws. The profile is the pot's
  // wall seen in section, so mirroring it about a vertical axis gives the
  // form. Geometry comes from ribGeometry(), the same resolved path the rib
  // view and the SVG export use, so the preview can never disagree with what
  // was drawn.
  //
  // Nothing here is stored. How far out the rib is held is the customer's
  // choice at the wheel, not part of the design, so it never reaches the cart.

  var potOffset = null;        // mm added to every radius; null = not yet set
  var POT_MIN_BASE = 45;       // a foot narrower than this looks unstable

  // The profile need not touch the spine. Mirrored as drawn it would leave a
  // hole through the middle, so push it out far enough to close the base.
  function potFloorOffset(pts){
    return Math.max(0, POT_MIN_BASE/2 - pts[0].x);
  }

  // Open on a plausible pot: a sensible base and a widest point around three
  // quarters of the height. The same rule the pot renders use.
  function potDefaultOffset(pts, H){
    var mx = 0;
    for(var i=0;i<pts.length;i++){ if(pts[i].x > mx) mx = pts[i].x; }
    return Math.max(0, Math.round(0.38*H - mx - potFloorOffset(pts)));
  }

  // The profile as {x, y} in mm with y measured from the base, sampled along
  // the resolved curve rather than just the nodes.
  function potProfilePoints(){
    var geo = ribGeometry();
    var out = [];
    for(var i=0;i<geo.samples.length;i++){
      out.push({x: geo.samples[i].x, y: geo.H - geo.samples[i].y});
    }
    out.sort(function(a,b){ return a.y - b.y; });
    return out;
  }

  function renderPotCanvas(){
    var svg = ROOT.getElementById('potCanvas');
    if(!svg) return;
    var P = potProfilePoints();
    if(P.length < 2){ svg.innerHTML = ''; return; }

    var H = state.totalHeight;
    var floorOff = potFloorOffset(P);
    if(potOffset === null){ potOffset = potDefaultOffset(P, H); }
    var off = floorOff + potOffset;

    var R = P.map(function(p){ return {y:p.y, x:p.x + off}; });
    var maxR = 0;
    for(var i=0;i<R.length;i++){ if(R[i].x > maxR) maxR = R[i].x; }
    var baseNow = R[0].x, rimNow = R[R.length-1].x;

    var W = 380, TOP = 14, BOT = 316;
    // The drawing is taller and wider than the pot: the rim ellipse rises
    // above it, the foot and shadow drop below, and the shadow spreads wider
    // than the base. Scale to fit all of it, so nothing is ever cut off.
    var ELL = 0.19, SHADOW = 1.15, SHADOW_RY = 0.28, SHADOW_DROP = 7;
    var sc = Math.min(
      (BOT - TOP - SHADOW_DROP) / (H + rimNow*ELL + baseNow*SHADOW_RY),
      (W/2 - 12) / Math.max(maxR, baseNow*SHADOW)
    );

    var potH = H*sc;
    var baseY = (TOP + BOT)/2 + potH/2;
    baseY += (rimNow*ELL*sc - (baseNow*SHADOW_RY*sc + SHADOW_DROP))/2;
    var cx = W/2;
    var Y  = function(mm){ return baseY - mm*sc; };
    var XR = function(mm){ return cx + mm*sc; };
    var XL = function(mm){ return cx - mm*sc; };

    function pathThrough(pts, X){
      var d = 'M'+X(pts[0].x)+','+Y(pts[0].y);
      for(var i=1;i<pts.length;i++){ d += ' L'+X(pts[i].x)+','+Y(pts[i].y); }
      return d;
    }

    var o = [];
    o.push('<defs>'
      + '<linearGradient id="potBody" x1="0" y1="0" x2="1" y2="0">'
      +   '<stop offset="0%" stop-color="#785E45"/><stop offset="14%" stop-color="#A5866A"/>'
      +   '<stop offset="34%" stop-color="#D2B491"/><stop offset="55%" stop-color="#BD9C79"/>'
      +   '<stop offset="80%" stop-color="#8E7355"/><stop offset="100%" stop-color="#6B543C"/>'
      + '</linearGradient>'
      + '<linearGradient id="potInside" x1="0" y1="0" x2="1" y2="0">'
      +   '<stop offset="0%" stop-color="#4A3A2A"/><stop offset="45%" stop-color="#6A5540"/>'
      +   '<stop offset="100%" stop-color="#3F3123"/></linearGradient>'
      + '<radialGradient id="potShadow" cx="50%" cy="50%" r="50%">'
      +   '<stop offset="0%" stop-color="rgba(90,70,50,0.28)"/>'
      +   '<stop offset="100%" stop-color="rgba(90,70,50,0)"/></radialGradient></defs>');

    o.push('<ellipse cx="'+cx+'" cy="'+(Y(0)+SHADOW_DROP)+'" rx="'+(baseNow*sc*SHADOW)
      + '" ry="'+(baseNow*sc*SHADOW_RY)+'" fill="url(#potShadow)"/>');

    var baseRy = Math.max(2.5, baseNow*sc*ELL);
    var right = pathThrough(R, XR);
    var revs = R.slice().reverse();
    var leftDown = pathThrough(revs, XL).replace(/^M[^L]*/, '');
    var body = right + ' L'+XL(rimNow)+','+Y(H) + leftDown
             + ' A'+(baseNow*sc)+','+baseRy+' 0 0 0 '+XR(baseNow)+','+Y(0)+' Z';
    o.push('<path d="'+body+'" fill="url(#potBody)"/>');

    // A wall has thickness, so the rim is a ring rather than a line.
    var ry = Math.max(3, rimNow*sc*ELL);
    var WALL = 3;
    var innerR = Math.max(rimNow - WALL, rimNow*0.55);
    var innerRy = Math.max(2, innerR*sc*ELL);
    var drop = Math.max(1.2, WALL*sc*0.30);
    o.push('<ellipse cx="'+cx+'" cy="'+Y(H)+'" rx="'+(rimNow*sc)+'" ry="'+ry+'" fill="url(#potBody)"/>');
    o.push('<ellipse cx="'+cx+'" cy="'+(Y(H)+drop)+'" rx="'+(innerR*sc)+'" ry="'+innerRy+'" fill="url(#potInside)"/>');
    o.push('<ellipse cx="'+cx+'" cy="'+Y(H)+'" rx="'+(rimNow*sc)+'" ry="'+ry+'" fill="none" stroke="#5E4A35" stroke-width="1.3"/>');
    o.push('<ellipse cx="'+cx+'" cy="'+(Y(H)+drop)+'" rx="'+(innerR*sc)+'" ry="'+innerRy+'" fill="none" stroke="#4A3A2A" stroke-width="1"/>');

    // Outline the sides and foot only: stroking the whole body would trace
    // the line across the top of the rim, straight through the opening.
    o.push('<path d="'+right+'" fill="none" stroke="#5E4A35" stroke-width="1.4"/>');
    o.push('<path d="M'+XL(rimNow)+','+Y(H)+leftDown+'" fill="none" stroke="#5E4A35" stroke-width="1.4"/>');
    o.push('<path d="M'+XL(baseNow)+','+Y(0)+' A'+(baseNow*sc)+','+baseRy+' 0 0 0 '+XR(baseNow)+','+Y(0)
      + '" fill="none" stroke="#5E4A35" stroke-width="1.4"/>');

    svg.innerHTML = o.join('');

    // Report in whichever the customer is working in - the preview is the pot,
    // so in fired mode it should read as the finished piece.
    var shown = function(mm){ return Math.round(firedMode() ? toFired(mm) : mm); };
    var set = function(id, v){ var el = ROOT.getElementById(id); if(el) el.textContent = v; };
    set('potWidest', shown(maxR*2) + 'mm');
    set('potHeight', shown(H) + 'mm');
    set('potBase',   shown(baseNow*2) + 'mm');
    set('potRim',    shown(rimNow*2) + 'mm');
  }

  var potPanelWired = false;
  function renderPotPanel(){
    var slider = ROOT.getElementById('potWidth');
    if(slider){
      slider.max = 70;
      slider.value = (potOffset === null) ? 0 : potOffset;
      slider.disabled = locked;
    }
    if(potPanelWired) return;
    var panel = ROOT.getElementById('potView');
    if(!panel) return;
    potPanelWired = true;
    panel.addEventListener('input', function(evt){
      if(!evt.target || evt.target.id !== 'potWidth') return;
      potOffset = parseFloat(evt.target.value) || 0;
      renderPotCanvas();
    });
  }

  // Messages shown while on the rib view, where the profile view's own
  // warning element is hidden.
  function showRibNote(msg){
    var el = ROOT.getElementById('ribNote');
    if(!el) return;
    el.textContent = msg || '';
    el.style.display = msg ? 'block' : 'none';
  }

  // Switching views. Editing the profile can invalidate the hole position,
  // so warn first and then move it to the nearest spot that still fits.
  function setView(v){
    if(v === state.view) return;
    state.view = v;
    if(v === 'profile'){ profileEditWarned = false; }
    // Re-pick a sensible width each time the preview is opened: the profile
    // may have changed since last time, and a width chosen for the old shape
    // can be a poor fit for the new one.
    if(v === 'pot'){ potOffset = null; }
    if(v === 'rib' && state.hole){
      var geo = ribGeometry();
      var pos = clampHole(state.hole.x, state.hole.y, state.hole.d, geo);
      if(pos){ state.hole.x = pos.x; state.hole.y = pos.y; }
      else {
        var d = largestHoleThatFits(spacerCentre(geo), geo.H/2, geo);
        var fallback = d >= HOLE_MIN_D ? clampHole(spacerCentre(geo), geo.H/2, d, geo) : null;
        if(fallback){ state.hole = {x:fallback.x, y:fallback.y, d:d}; }
        else { state.hole = null; }
      }
    }
    render();
  }

  // Informational, not blocking: the edit has already been made, so this
  // tells them to check the hole rather than asking permission.
  var holeReviewEl = null;
  function showHoleReviewWarning(){
    if(!holeReviewEl){
      var overlay = document.createElement('div');
      overlay.setAttribute('role','dialog');
      overlay.setAttribute('aria-modal','true');
      overlay.setAttribute('aria-label','Review your hole');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483600;background:rgba(23,23,23,0.55);display:flex;align-items:center;justify-content:center;padding:20px;';
      var card = document.createElement('div');
      card.style.cssText = 'background:#FFFFFF;border-radius:12px;padding:22px;max-width:380px;width:100%;box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#171717;';
      var t = document.createElement('div');
      t.textContent = 'Check Your Hole Position';
      t.style.cssText = 'font-size:16px;font-weight:500;margin:0 0 8px;';
      var p = document.createElement('div');
      p.textContent = 'Changing the profile can move the hole, since it always stays at least 3mm inside every edge. Have a look at the Rib & Hole step before adding to your cart.';
      p.style.cssText = 'font-size:13.5px;line-height:1.55;margin:0 0 18px;color:#473728;';
      var row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:10px;';
      var go = document.createElement('button');
      go.type='button'; go.textContent='Review It Now';
      go.style.cssText = 'flex:1;font-family:inherit;font-size:13.5px;padding:11px 12px;border-radius:8px;border:1px solid #473728;background:#473728;color:#FFFFFF;cursor:pointer;';
      var stay = document.createElement('button');
      stay.type='button'; stay.textContent='Keep Editing';
      stay.style.cssText = 'flex:1;font-family:inherit;font-size:13.5px;padding:11px 12px;border-radius:8px;border:1px solid #D2C1B4;background:#FFFFFF;color:#473728;cursor:pointer;';
      go.addEventListener('click', function(){ overlay.style.display='none'; setView('rib'); });
      stay.addEventListener('click', function(){ overlay.style.display='none'; });
      overlay.addEventListener('keydown', function(e){ if(e.key==='Escape'){ overlay.style.display='none'; } });
      row.appendChild(go); row.appendChild(stay);
      card.appendChild(t); card.appendChild(p); card.appendChild(row);
      overlay.appendChild(card);
      holeReviewEl = overlay;
      holeReviewEl.__stay = stay;
    }
    if(!holeReviewEl.parentNode){ document.body.appendChild(holeReviewEl); }
    holeReviewEl.style.display = 'flex';
    try { holeReviewEl.__stay.focus(); } catch(e){}
  }

  function renderViewTabs(){
    var row = ROOT.getElementById('viewTabs');
    if(!row) return;
    row.innerHTML =
      '<button type="button" data-view="profile" class="'+(state.view==='profile'?'active':'')+'"><span class="num">1. </span>Profile</button>' +
      '<button type="button" data-view="rib" class="'+(state.view==='rib'?'active':'')+'"><span class="num">2. </span>Rib &amp; Hole</button>' +
      '<button type="button" data-view="pot" class="'+(state.view==='pot'?'active':'')+'"><span class="num">3. </span>Pot Preview</button>';
    Array.prototype.forEach.call(row.querySelectorAll('button[data-view]'), function(b){
      b.addEventListener('click', function(){ setView(b.getAttribute('data-view')); });
    });
    var pv = ROOT.getElementById('profileView');
    var rv = ROOT.getElementById('ribView');
    var ov = ROOT.getElementById('potView');
    if(pv) pv.style.display = (state.view==='profile') ? '' : 'none';
    if(rv) rv.style.display = (state.view==='rib') ? '' : 'none';
    if(ov) ov.style.display = (state.view==='pot') ? '' : 'none';
  }

  function render(){
    if(typeof syncDesignToForm === 'function'){ try { syncDesignToForm(); } catch(e){} }
    renderViewTabs();
    if(state.view === 'pot'){
      renderPotPanel();
      renderPotCanvas();
    } else if(state.view === 'rib'){
      renderRibCanvas();
      renderRibPanel();
      wireRibPanel();
    } else {
      renderToolbar();
      renderCanvas();
      renderSelectionPanel();
    }
    var undoLink = ROOT.getElementById('undoLink');
    if(undoLink){ undoLink.classList.toggle('disabled', history.length===0 || locked); }
    ['mirrorTopLink','mirrorBottomLink','resetLink'].forEach(function(id){
      var el = ROOT.getElementById(id);
      if(el){ el.classList.toggle('disabled', locked); }
    });
    ROOT.getElementById('heightInput').disabled = locked;
    if(typeof refreshShrinkUI === 'function'){ try { refreshShrinkUI(); } catch(e){} }
    ROOT.getElementById('uploadBtn').disabled = locked;
    var lockBtn = ROOT.getElementById('lockBtn');
    if(lockBtn){ lockBtn.disabled = locked; lockBtn.textContent = locked ? 'Design Confirmed' : 'Confirm Design'; }
  }

  function computeHint(){
    if(state.activeTool==='sketch-smooth'){
      return 'Draw down or up the canvas in one stroke — comes out as smooth curves, any backtrack gets trimmed.';
    }
    if(state.activeTool==='sketch-line'){
      return 'Draw down or up the canvas in one stroke — comes out as straight segments, any backtrack gets trimmed.';
    }
    if(state.selectedNodes.length>0){
      var idx = state.selectedNodes[0];
      var isEndpoint = (idx===0 || idx===state.nodes.length-1);
      return isEndpoint
        ? 'Drag this point sideways to move it, or set an exact length below.'
        : 'Drag this point to move it, or double-click it to remove it.';
    }
    if(state.selectedBands.length>0){
      if(state.curvatureArmedBand!==null){
        return 'Drag this curve to reshape it.';
      }
      if(state.selectedBands.length>1){
        return 'Drag any of the selected segments — the rest move or reshape together with it.';
      }
      var bi = state.selectedBands[0];
      return state.types[bi]==='curve'
        ? (isCoarsePointer()
            ? 'Drag to move this curve. Press and hold it, or use Reshape Curve, to bend it.'
            : 'Drag to move this curve, or double-click it, then drag, to reshape it.')
        : 'Drag to move this line, or use Vertical / Horizontal to straighten it.';
    }
    return isCoarsePointer()
      ? 'Tap empty space to add a point. Drag a point or segment to move it. Press and hold a curve to bend it.'
      : 'Click empty space to add a point. Drag a point or segment to move it. Double-click a curve to reshape it.';
  }

  function renderToolbar(){
    var toolRow = ROOT.getElementById('toolTypeRow');
    var manualCls = state.activeTool==='edit' ? 'active' : '';
    var smoothCls = state.activeTool==='sketch-smooth' ? 'active' : '';
    var lineCls = state.activeTool==='sketch-line' ? 'active' : '';
    var dis = locked ? 'disabled' : '';
    toolRow.innerHTML = '<button data-tool="edit" class="'+manualCls+'" '+dis+'>Manual</button><button data-tool="sketch-smooth" class="'+smoothCls+'" '+dis+'>Smooth Draw</button><button data-tool="sketch-line" class="'+lineCls+'" '+dis+'>Line Draw</button>';
    if(!locked){
      Array.prototype.forEach.call(toolRow.querySelectorAll('button[data-tool]'), function(btn){
        btn.addEventListener('click', function(){
          state.activeTool = btn.getAttribute('data-tool');
          render();
        });
      });
    }
    ROOT.getElementById('hintText').textContent = locked
      ? 'Design confirmed.'
      : computeHint();
  }

  // Which set of numbers leads on the grid. The drawing never changes - only
  // the labels - because the design is always the actual rib.
  var FIRED_COL = '#7E9070', RIB_COL = '#A4886E';
  function leadMm(mm){ return firedMode() ? toFired(mm) : mm; }
  function secondMm(mm){ return firedMode() ? mm : toFired(mm); }

  function renderCanvas(){
    var parts = [];
    var LEAD_COL   = firedMode() ? FIRED_COL : RIB_COL;
    var SECOND_COL = firedMode() ? RIB_COL : FIRED_COL;
    // The grid is drawn in the LEADING scale's units - the numbers on the
    // left and bottom. Stepping in rib millimetres and labelling the fired
    // equivalent gave a fired scale reading 9, 18, 26 instead of 10, 20, 30.
    // A position in lead units converts back to rib millimetres for drawing.
    var toRib = function(lead){ return firedMode() ? lead / shrinkK() : lead; };
    var leadH = leadMm(state.totalHeight);
    var leadX = leadMm(xRange());
    var majorStep = chooseStep(leadH);
    var minorStep = 1;

    for(var gy=0; gy<=leadH+0.001; gy+=minorStep){
      var ribY = toRib(gy);
      if(ribY > state.totalHeight + 0.001) break;
      var sy = nodeYToSvgY(ribY);
      var isMajor = Math.abs(gy - Math.round(gy/majorStep)*majorStep) < 0.01;
      parts.push('<line x1="'+AXIS_X+'" y1="'+sy+'" x2="'+PLOT_RIGHT+'" y2="'+sy+'" stroke="'+(isMajor?'#D2C1B4':'#E3E0DF')+'" stroke-width="'+(isMajor?'1':'0.5')+'"/>');
      // Leave room at both ends: the 0 and full-height labels are drawn
      // separately, and a major step landing a millimetre from either of them
      // prints two numbers on top of each other.
      var endGap = Math.max(3, leadH*0.035);
      if(isMajor && gy > endGap && gy < leadH - endGap){
        parts.push('<line x1="'+(AXIS_X-4)+'" y1="'+sy+'" x2="'+AXIS_X+'" y2="'+sy+'" stroke="#A4886E" stroke-width="1"/>');
        parts.push('<text x="'+(AXIS_X-8)+'" y="'+(sy+4)+'" text-anchor="end" class="gnum" font-size="9" fill="'+LEAD_COL+'">'+Math.round(gy)+'</text>');
        if(shrinkPct > 0){
          parts.push('<text x="'+(PLOT_RIGHT+8)+'" y="'+(sy+4)+'" text-anchor="start" class="gnum" font-size="9" fill="'+SECOND_COL+'">'+Math.round(secondMm(ribY))+'</text>');
        }
      }
    }

    for(var gx=minorStep; gx<=leadX+0.001; gx+=minorStep){
      var ribX = toRib(gx);
      if(ribX > xRange() + 0.001) break;
      var sx = nodeXToSvgX(ribX);
      var isMajorX = Math.abs(gx - Math.round(gx/majorStep)*majorStep) < 0.01;
      parts.push('<line x1="'+sx+'" y1="'+AXIS_TOP+'" x2="'+sx+'" y2="'+AXIS_BOTTOM+'" stroke="'+(isMajorX?'#D2C1B4':'#E3E0DF')+'" stroke-width="'+(isMajorX?'1':'0.5')+'"/>');
      if(isMajorX){
        parts.push('<line x1="'+sx+'" y1="'+AXIS_BOTTOM+'" x2="'+sx+'" y2="'+(AXIS_BOTTOM+6)+'" stroke="#A4886E" stroke-width="1"/>');
        parts.push('<line x1="'+sx+'" y1="'+(AXIS_TOP-6)+'" x2="'+sx+'" y2="'+AXIS_TOP+'" stroke="#A4886E" stroke-width="1"/>');
        parts.push('<text x="'+sx+'" y="428" text-anchor="middle" class="gnum" font-size="9" fill="'+LEAD_COL+'">'+Math.round(gx)+'</text>');
        parts.push('<text x="'+sx+'" y="25" text-anchor="middle" class="gnum" font-size="9" fill="'+(shrinkPct>0?SECOND_COL:LEAD_COL)+'">'+Math.round(shrinkPct>0?secondMm(ribX):gx)+'</text>');
      }
    }

    parts.push('<rect id="bgCapture" x="0" y="0" width="274" height="448" fill="transparent" style="touch-action:none;cursor:crosshair;"/>');

    parts.push('<path d="'+fillPathD()+'" fill="rgba(164,136,110,0.18)" stroke="none" style="pointer-events:none;"/>');

    parts.push('<line x1="'+AXIS_X+'" y1="'+AXIS_BOTTOM+'" x2="'+AXIS_X+'" y2="'+AXIS_TOP+'" stroke="#A4886E" stroke-width="1"/>');
    parts.push('<line x1="'+(AXIS_X-6)+'" y1="'+AXIS_BOTTOM+'" x2="'+(AXIS_X+6)+'" y2="'+AXIS_BOTTOM+'" stroke="#A4886E" stroke-width="1"/>');
    parts.push('<line x1="'+(AXIS_X-6)+'" y1="'+AXIS_TOP+'" x2="'+(AXIS_X+6)+'" y2="'+AXIS_TOP+'" stroke="#A4886E" stroke-width="1"/>');
    parts.push('<text x="'+(AXIS_X-8)+'" y="'+(AXIS_BOTTOM+4)+'" text-anchor="end" class="gend" font-size="10" fill="#473728">0</text>');
    parts.push('<text x="'+(AXIS_X-8)+'" y="'+(AXIS_TOP+4)+'" text-anchor="end" class="gend" font-size="10" fill="#473728">'+Math.round(leadMm(state.totalHeight))+'</text>');
    if(shrinkPct > 0){
      parts.push('<text x="'+(PLOT_RIGHT+8)+'" y="'+(AXIS_BOTTOM+4)+'" text-anchor="start" class="gend" font-size="10" fill="'+SECOND_COL+'">0</text>');
      parts.push('<text x="'+(PLOT_RIGHT+8)+'" y="'+(AXIS_TOP+4)+'" text-anchor="start" class="gend" font-size="10" fill="'+SECOND_COL+'">'+Math.round(secondMm(state.totalHeight))+'</text>');
      // Every edge named, so nobody has to work out which column is which.
      // The leading scale runs down the left and along the bottom; the other
      // down the right and along the top. The canvas was widened to 296 to
      // make room on the right - the plot itself has not moved.
      var MIDY = (AXIS_TOP + AXIS_BOTTOM)/2, MIDX = (AXIS_X + PLOT_RIGHT)/2;
      var leadWord = firedMode() ? 'fired' : 'rib';
      var secWord  = firedMode() ? 'rib' : 'fired';
      var axisLabel = function(x, y, text, col, rot){
        parts.push('<text x="'+x+'" y="'+y+'" text-anchor="middle" class="gaxis" font-size="8.5" fill="'+col+'"'
          + ' letter-spacing="0.4"' + (rot ? ' transform="rotate(-90 '+x+' '+y+')"' : '')
          + ' style="pointer-events:none;user-select:none;">'+text+'</text>');
      };
      var caps = function(w){ return w.toUpperCase(); };
      axisLabel(11,   MIDY, caps(leadWord) + ' HEIGHT (MM)', LEAD_COL,   true);
      axisLabel(269,  MIDY, caps(secWord)  + ' HEIGHT (MM)', SECOND_COL, true);
      axisLabel(MIDX, 443,  caps(leadWord) + ' WIDTH (MM)',  LEAD_COL,   false);
      axisLabel(MIDX, 9,    caps(secWord)  + ' WIDTH (MM)',  SECOND_COL, false);
    }

    for(var i=0;i<state.types.length;i++){
      var d = pathForBand(i);
      var isSel = state.selectedBands.indexOf(i) !== -1;
      var isArmed = state.curvatureArmedBand === i;
      var col = isSel ? '#473728' : '#A4886E';
      var w2 = isSel ? 3.5 : 2.5;
      var dash = isArmed ? ' stroke-dasharray="5 3"' : '';
      parts.push('<path d="'+d+'" fill="none" stroke="'+col+'" stroke-width="'+w2+'"'+dash+'/>');
      parts.push('<path class="hitband" data-band="'+i+'" d="'+d+'" fill="none" stroke="transparent" stroke-width="16" style="pointer-events:stroke;cursor:pointer;touch-action:none;"/>');
    }

    if(dragState && dragState.kind==='sketch' && dragState.raw.length>1){
      var pts = dragState.raw.map(function(p){return p.x+','+p.y;}).join(' ');
      parts.push('<polyline points="'+pts+'" fill="none" stroke="#A4886E" stroke-width="1.5" stroke-dasharray="3 3"/>');
    }

    state.nodes.forEach(function(n,i){
      var sxn = nodeXToSvgX(n.x), syn = nodeYToSvgY(n.y);
      var isSelN = state.selectedNodes.indexOf(i) !== -1;
      var r = isSelN ? 6.5 : 4.5;
      var stroke = isSelN ? '#473728' : '#fff';
      parts.push('<circle cx="'+sxn+'" cy="'+syn+'" r="'+r+'" fill="#A4886E" stroke="'+stroke+'" stroke-width="1.75" style="pointer-events:none;"/>');
    });
    // Which side is the pot and which is the tool. Fixed positions, because
    // labels that reposition themselves while a point is being dragged are
    // distracting. Skipped where the profile would run through them - better
    // no label than one with a curve drawn across it.
    (function(){
      var LABEL_Y = (AXIS_TOP + AXIS_BOTTOM)/2;
      var CLEAR = 16;              // keep this far from the curve
      var potX = AXIS_X + 17;
      var ribX = PLOT_RIGHT - 11;

      // The label is rotated, so it occupies a short band around LABEL_Y - not
      // the whole height. Only the part of the profile beside that band can
      // actually collide with it. Testing every height made a point at the top
      // of the canvas hide a label in the middle.
      var HALF = 42;               // half the label's length, once rotated
      var xsAt = [];
      for(var t=0; t<=80; t++){
        var yy = state.totalHeight * t/80;
        var sy = nodeYToSvgY(yy);
        if(sy < LABEL_Y - HALF || sy > LABEL_Y + HALF) continue;
        xsAt.push(nodeXToSvgX(profileXAtHeight(yy)));
      }
      var near = function(lx){
        return xsAt.some(function(cx){ return Math.abs(cx-lx) < CLEAR; });
      };
      var label = function(x, text){
        parts.push('<text x="'+x+'" y="'+LABEL_Y+'" text-anchor="middle" font-size="12"'
          + ' class="gside" fill="#C4B2A2" letter-spacing="1.5" transform="rotate(-90 '+x+' '+LABEL_Y+')"'
          + ' style="pointer-events:none;user-select:none;">'+text+'</text>');
      };
      if(!near(potX)) label(potX, 'POT SIDE');
      if(!near(ribX)) label(ribX, 'THE RIB');
    })();

    // Hit areas last, so they sit above every drawn element and a tap near a
    // point always reaches the point rather than the segment beneath it.
    state.nodes.forEach(function(n,i){
      var sxn = nodeXToSvgX(n.x), syn = nodeYToSvgY(n.y);
      parts.push('<circle class="nodept" data-node="'+i+'" cx="'+sxn+'" cy="'+syn+'" r="'+NODE_HIT_R+'" fill="transparent" style="cursor:grab;touch-action:none;"/>');
    });

    svg.innerHTML = parts.join('');
    attachCanvasHandlers();
  }

  function attachCanvasHandlers(){
    if(locked) return;
    Array.prototype.forEach.call(svg.querySelectorAll('.hitband'), function(el){
      var idx = parseInt(el.getAttribute('data-band'),10);

      el.addEventListener('pointerdown', function(evt){
        if(isSketchMode()) return;
        evt.stopPropagation();
        var pt = getSvgPoint(evt);
        var d = {kind:'band', index:idx, startX:pt.x, startY:pt.y, moved:false};
        var isSelected = state.selectedBands.indexOf(idx)!==-1;
        var armed = (state.curvatureArmedBand===idx) && state.types[idx]==='curve';
        if(!armed){ startLongPress(idx); }

        if(armed){
          d.mode = 'bulge';
          var groupIndices = (isSelected && state.selectedBands.length>1) ? state.selectedBands : [idx];
          d.startBulgeOfDragged = state.bulges[idx];
          d.startBulges = {};
          groupIndices.forEach(function(bi){
            if(state.types[bi]==='curve'){ d.startBulges[bi] = state.bulges[bi]; }
          });
        } else {
          d.mode = 'translate';
          var groupBands = (isSelected && state.selectedBands.length>1) ? state.selectedBands : [idx];
          var nodeSet = {};
          groupBands.forEach(function(bi){ nodeSet[bi]=true; nodeSet[bi+1]=true; });
          d.groupNodeIndices = Object.keys(nodeSet).map(function(s){ return parseInt(s,10); });
          d.startNodeX = {};
          d.groupNodeIndices.forEach(function(ni){ d.startNodeX[ni] = state.nodes[ni].x; });
        }
        dragState = d;
      });
    });

    Array.prototype.forEach.call(svg.querySelectorAll('.nodept'), function(el){
      var idx = parseInt(el.getAttribute('data-node'),10);
      el.addEventListener('pointerdown', function(evt){
        if(isSketchMode()) return;
        evt.stopPropagation();
        var pt = getSvgPoint(evt);
        dragState = {kind:'node', index:idx, startX:pt.x, startY:pt.y, moved:false};
      });
    });

    var bg = ROOT.getElementById('bgCapture');
    bg.addEventListener('pointerdown', function(evt){
      if(isSketchMode()){
        dragState = {kind:'sketch', raw:[getSvgPoint(evt)]};
        renderCanvas();
      } else {
        addPointAt(getSvgPoint(evt));
      }
    });
  }

  function addPointAt(svgPt){
    var y = clamp(svgYToNodeY(svgPt.y), 0, state.totalHeight);
    var bandIdx = findBandForY(y);
    if(bandIdx === -1) return;
    var a = state.nodes[bandIdx], b = state.nodes[bandIdx+1];
    var minGap = state.totalHeight/25;
    if(y - a.y < minGap || b.y - y < minGap) return;

    pushHistory();
    var origType = state.types[bandIdx];
    var origBulge = state.bulges[bandIdx];
    var x = clamp(svgXToNodeX(svgPt.x), 0, xRange());
    var bulgeRatio = 0;
    if(origType==='curve'){
      var g = bandGeometry(bandIdx);
      bulgeRatio = origBulge / g.len;
    }

    state.nodes.splice(bandIdx+1, 0, {y:y, x:x});
    state.types.splice(bandIdx, 1, origType, origType);

    if(origType==='curve'){
      var gLeft = bandGeometry(bandIdx);
      var gRight = bandGeometry(bandIdx+1);
      state.bulges.splice(bandIdx, 1, bulgeRatio*gLeft.len, bulgeRatio*gRight.len);
    } else {
      state.bulges.splice(bandIdx, 1, origBulge, origBulge);
    }

    clearSelection();
    render();
  }

  function removeNodeInternal(idx){
    var keepType = state.types[idx-1];
    var keepBulge = state.bulges[idx-1];
    state.nodes.splice(idx,1);
    state.types.splice(idx-1,2,keepType);
    state.bulges.splice(idx-1,2,keepBulge);
  }

  function removeNode(idx){
    if(idx===0 || idx===state.nodes.length-1) return;
    pushHistory();
    removeNodeInternal(idx);
    clearSelection();
    render();
  }

  function applyBandHeightSnap(val){
    if(state.selectedBands.length===0) return null;
    var maxIdx = Math.max.apply(null, state.selectedBands);
    var targetNode = maxIdx+1;
    if(targetNode >= state.nodes.length-1) return null;
    state.nodes[targetNode].y = Math.round(safeYClamp(targetNode, val));
    renderCanvas();
    return state.nodes[targetNode].y;
  }

  function applyNodeHeightSnap(val){
    var idx = state.selectedNodes[0];
    if(idx===undefined || idx===0 || idx===state.nodes.length-1) return null;
    state.nodes[idx].y = Math.round(safeYClamp(idx, val));
    renderCanvas();
    return state.nodes[idx].y;
  }

  function applyNodeLengthSnap(val){
    var idx = state.selectedNodes[0];
    if(idx===undefined) return null;
    state.nodes[idx].x = Math.round(clamp(val, 0, xRange()));
    renderCanvas();
    return state.nodes[idx].x;
  }

  document.addEventListener('pointermove', function(evt){
    if(!dragState) return;
    var pt = getSvgPoint(evt);
    if(dragState.kind==='node'){
      var idx = dragState.index;
      var n = state.nodes[idx];
      var isEndpoint = (idx===0 || idx===state.nodes.length-1);
      var dx0 = pt.x-dragState.startX, dy0 = pt.y-dragState.startY;
      if(Math.sqrt(dx0*dx0+dy0*dy0) > 4 && !dragState.moved){
        dragState.moved = true;
        pushHistory();
      }
      if(!isEndpoint){
        n.y = safeYClamp(idx, svgYToNodeY(pt.y));
      }
      n.x = clamp(svgXToNodeX(pt.x), 0, xRange());
      renderCanvas();
    } else if(dragState.kind==='band'){
      var dxb = pt.x-dragState.startX, dyb = pt.y-dragState.startY;
      if(Math.sqrt(dxb*dxb+dyb*dyb) > 4 && !dragState.moved){
        dragState.moved = true;
        cancelLongPress();
        pushHistory();
      }
      if(dragState.moved && dragState.mode==='bulge'){
        var g = bandGeometry(dragState.index);
        var midx = (g.ax+g.bx)/2, midy = (g.ay+g.by)/2;
        var proj = clamp((pt.x-midx)*g.px + (pt.y-midy)*g.py, -maxBulgePx(), maxBulgePx());
        var delta = proj - dragState.startBulgeOfDragged;
        Object.keys(dragState.startBulges).forEach(function(key){
          var bi = parseInt(key,10);
          state.bulges[bi] = clamp(dragState.startBulges[bi]+delta, -maxBulgePx(), maxBulgePx());
        });
        renderCanvas();
      } else if(dragState.moved && dragState.mode==='translate'){
        var deltaXmm = svgXToNodeX(pt.x) - svgXToNodeX(dragState.startX);
        dragState.groupNodeIndices.forEach(function(ni){
          state.nodes[ni].x = clamp(dragState.startNodeX[ni]+deltaXmm, 0, xRange());
        });
        renderCanvas();
      }
    } else if(dragState.kind==='sketch'){
      var last = dragState.raw[dragState.raw.length-1];
      var ddx = pt.x-last.x, ddy = pt.y-last.y;
      if(Math.sqrt(ddx*ddx+ddy*ddy) > 4){
        dragState.raw.push(pt);
        renderCanvas();
      }
    }
  });

  // Double-tap is unreliable on touch: browsers often claim it for zoom and
  // the timing window is fragile. Press and hold arms reshaping instead, and
  // the selection panel also carries an explicit button for anyone who never
  // discovers the gesture. Double-click still works with a mouse.
  var LONG_PRESS_MS = 500;

  function startLongPress(bandIndex){
    cancelLongPress();
    if(state.types[bandIndex] !== 'curve') return;
    longPressTimer = setTimeout(function(){
      longPressTimer = null;
      if(!dragState || dragState.kind !== 'band' || dragState.index !== bandIndex) return;
      if(dragState.moved) return;
      dragState.armedByHold = true;
      state.curvatureArmedBand = bandIndex;
      state.selectedBands = [bandIndex];
      state.selectedNodes = [];
      render();
    }, LONG_PRESS_MS);
  }
  function cancelLongPress(){
    if(longPressTimer){ clearTimeout(longPressTimer); longPressTimer = null; }
  }

  document.addEventListener('pointerup', function(){
    cancelLongPress();
    if(!dragState) return;
    if(dragState.kind==='band' && dragState.armedByHold){
      // Already armed by the hold; don't let the same tap toggle it off.
      // Clearing dragState here is essential: leaving it set makes the NEXT
      // tap anywhere re-enter this handler and re-render mid-click, which
      // destroys whatever button was being pressed before its click fires.
      dragState = null;
      render();
      return;
    }
    if(dragState.kind==='band'){
      if(!dragState.moved){
        var i = dragState.index;
        if(state.types[i]==='curve' && isDoubleClick('band', i)){
          state.curvatureArmedBand = i;
          if(state.selectedBands.indexOf(i)===-1){ state.selectedBands=[i]; }
          state.selectedNodes = [];
        } else {
          state.curvatureArmedBand = null;
          var pos = state.selectedBands.indexOf(i);
          if(pos===-1){ state.selectedBands.push(i); } else { state.selectedBands.splice(pos,1); }
          state.selectedNodes = [];
        }
      } else {
        clearSelection();
      }
      render();
    } else if(dragState.kind==='node'){
      if(!dragState.moved){
        var ni = dragState.index;
        if(isDoubleClick('node', ni)){
          removeNode(ni);
          dragState = null;
          return;
        } else {
          var posN = state.selectedNodes.indexOf(ni);
          if(posN===-1){ state.selectedNodes = [ni]; } else { state.selectedNodes.splice(posN,1); }
          state.selectedBands = [];
          state.curvatureArmedBand = null;
        }
      }
      render();
    } else if(dragState.kind==='sketch'){
      if(dragState.raw.length>1){ pushHistory(); }
      finalizeSketch(dragState.raw);
      state.activeTool = 'edit';
      dragState = null;
      render();
      return;
    }
    dragState = null;
  });

  function perpDistSigned(p,a,b){
    var dx=b.x-a.x, dy=b.y-a.y;
    var len=Math.sqrt(dx*dx+dy*dy)||1;
    var px=-dy/len, py=dx/len;
    return (p.x-a.x)*px + (p.y-a.y)*py;
  }
  function perpDist(p,a,b){ return Math.abs(perpDistSigned(p,a,b)); }

  function rdpWithBulge(points, epsilon){
    if(points.length<3){
      return {pts:[points[0], points[points.length-1]], bulges:[0]};
    }
    var dmax=0, idx=0;
    for(var i=1;i<points.length-1;i++){
      var d=perpDist(points[i], points[0], points[points.length-1]);
      if(d>dmax){ dmax=d; idx=i; }
    }
    if(dmax>epsilon){
      var left=rdpWithBulge(points.slice(0,idx+1), epsilon);
      var right=rdpWithBulge(points.slice(idx), epsilon);
      return {
        pts: left.pts.slice(0,-1).concat(right.pts),
        bulges: left.bulges.concat(right.bulges)
      };
    }
    var bulgeVal = perpDistSigned(points[idx], points[0], points[points.length-1]);
    return {pts:[points[0], points[points.length-1]], bulges:[bulgeVal]};
  }

  function finalizeSketch(rawPts){
    if(rawPts.length < 2) return;
    var withY = rawPts.map(function(p){ return {svg:p, y: svgYToNodeY(p.y)}; });
    withY = withY.filter(function(o){ return o.y>=0 && o.y<=state.totalHeight; });
    if(withY.length<2) return;

    var decreasing = withY[withY.length-1].y < withY[0].y;
    var trimmed = [withY[0]];
    for(var i=1;i<withY.length;i++){
      var last = trimmed[trimmed.length-1];
      if(decreasing){
        if(withY[i].y <= last.y){ trimmed.push(withY[i]); }
      } else {
        if(withY[i].y >= last.y){ trimmed.push(withY[i]); }
      }
    }
    if(decreasing){ trimmed.reverse(); }
    if(trimmed.length<2) return;

    var svgPts = trimmed.map(function(o){ return o.svg; });

    var epsilonPx = Math.max(4, state.totalHeight*0.05) * scale();
    var result = rdpWithBulge(svgPts, epsilonPx);
    if(result.pts.length<2) return;

    var nodePts = result.pts.map(function(p){
      return {y: svgYToNodeY(p.y), x: clamp(svgXToNodeX(p.x), 0, xRange())};
    });
    nodePts[0].y = 0;
    nodePts[nodePts.length-1].y = state.totalHeight;

    state.nodes = nodePts;
    if(state.activeTool==='sketch-line'){
      state.types = new Array(nodePts.length-1).fill('line');
      state.bulges = new Array(nodePts.length-1).fill(0);
    } else {
      state.types = new Array(nodePts.length-1).fill('curve');
      state.bulges = result.bulges.map(function(b){ return clamp(b, -maxBulgePx(), maxBulgePx()); });
    }
    clearSelection();
  }

  // Bound once to the container. Binding to the button itself meant a panel
  // rebuild between press and release could swallow the click — the same
  // failure that affected the rib panel controls.
  var selPanelWired = false;
  function wireSelectionPanel(){
    if(selPanelWired) return;
    var panel = ROOT.getElementById('selectionPanel');
    if(!panel) return;
    selPanelWired = true;
    panel.addEventListener('click', function(evt){
      if(locked) return;
      var t = evt.target;
      if(!t || t.id !== 'reshapeBtn') return;
      if(!state.selectedBands.length) return;
      var bi = state.selectedBands[0];
      state.curvatureArmedBand = (state.curvatureArmedBand === bi) ? null : bi;
      render();
    });
  }

  function renderSelectionPanel(){
    wireSelectionPanel();
    var el = ROOT.getElementById('selectionPanel');
    if(locked){
      el.innerHTML = '<p class="selection-empty">Design confirmed.</p>';
      return;
    }
    if(state.selectedNodes.length > 0){
      renderNodePanel(el);
    } else if(state.selectedBands.length > 0){
      renderBandPanel(el);
    } else {
      el.innerHTML = '';
    }
  }

  function renderNodePanel(el){
    var onlyIdx = state.selectedNodes[0];
    var isEndpoint = (onlyIdx===0 || onlyIdx===state.nodes.length-1);
    var adjBands = adjacentBandsForNodes(state.selectedNodes);
    var isCurveJoint = isCurveAt(onlyIdx-1) && isCurveAt(onlyIdx);

    var adjAllLine = adjBands.length>0 && adjBands.every(function(bi){ return state.types[bi]==='line'; });
    var adjAllCurve = adjBands.length>0 && adjBands.every(function(bi){ return state.types[bi]==='curve'; });

    var html = '<div class="field-row"><label>Point selected</label>';
    if(!adjAllLine){ html += '<button id="nodeMakeLine">Make Line</button>'; }
    if(!adjAllCurve){ html += '<button id="nodeMakeCurve">Make Curve</button>'; }
    html += '</div>';

    if(isCurveJoint){
      html += '<div class="field-row"><button id="smoothPointBtn">Smooth This Point</button></div>';
    }

    html += '<div class="field-row">';
    if(!isEndpoint){
      html += '<label>Y Position</label><input type="number" step="1" id="nodeHeightSnap" value="'+Math.round(state.nodes[onlyIdx].y)+'"><span class="unit">mm</span>';
    }
    html += '<label>X Position</label><input type="number" step="1" min="0" id="nodeLengthSnap" value="'+Math.round(state.nodes[onlyIdx].x)+'"><span class="unit">mm</span>';
    html += '</div>';

    el.innerHTML = html;
    var nlBtn = ROOT.getElementById('nodeMakeLine');
    if(nlBtn){ nlBtn.addEventListener('click', function(){
      pushHistory();
      adjBands.forEach(function(bi){ state.types[bi]='line'; });
      render();
    }); }
    var ncBtn = ROOT.getElementById('nodeMakeCurve');
    if(ncBtn){ ncBtn.addEventListener('click', function(){
      pushHistory();
      adjBands.forEach(function(bi){ state.types[bi]='curve'; });
      render();
    }); }
    var spBtn = ROOT.getElementById('smoothPointBtn');
    if(spBtn){ spBtn.addEventListener('click', function(){ smoothPointJoint(onlyIdx); }); }
    var hInput = ROOT.getElementById('nodeHeightSnap');
    if(hInput){
      hInput.addEventListener('focus', function(){ pushHistory(); });
      hInput.addEventListener('input', function(evt){
        var v = parseFloat(evt.target.value);
        if(isNaN(v)) return;
        var result = applyNodeHeightSnap(v);
        if(result!==null){ evt.target.value = result; }
      });
    }
    var lInput = ROOT.getElementById('nodeLengthSnap');
    if(lInput){
      lInput.addEventListener('focus', function(){ pushHistory(); });
      lInput.addEventListener('input', function(evt){
        var v = parseFloat(evt.target.value);
        if(isNaN(v)) return;
        var result = applyNodeLengthSnap(v);
        if(result!==null){ evt.target.value = result; }
      });
    }
  }

  function applyVertical(){
    if(state.selectedBands.some(function(i){ return state.types[i]==='line'; })){ pushHistory(); }
    var lineBands = state.selectedBands.filter(function(i){ return state.types[i]==='line'; });
    lineBands.sort(function(a,b){return a-b;});
    lineBands.forEach(function(i){
      var a = state.nodes[i], b = state.nodes[i+1];
      var isFlat = Math.abs(b.y-a.y) < 0.5;
      if(isFlat){
        var curLen = Math.sqrt(Math.pow(b.x-a.x,2)+Math.pow(b.y-a.y,2)) || xRange()/4 || 8;
        var bIsFixedTop = (i+1 === state.nodes.length-1);
        var aIsFixedBottom = (i === 0);
        if(!bIsFixedTop){
          var upperBound = (i+2 < state.nodes.length) ? state.nodes[i+2].y : state.totalHeight;
          b.y = clamp(a.y+curLen, a.y, upperBound);
        } else if(!aIsFixedBottom){
          var lowerBound = (i-1>=0) ? state.nodes[i-1].y : 0;
          a.y = clamp(b.y-curLen, lowerBound, b.y);
        }
      }
      b.x = a.x;
    });
    render();
  }

  function applyHorizontal(){
    if(state.selectedBands.some(function(i){ return state.types[i]==='line'; })){ pushHistory(); }
    var lineBands = state.selectedBands.filter(function(i){ return state.types[i]==='line'; });
    lineBands.sort(function(a,b){return a-b;});
    lineBands.forEach(function(i){
      var a = state.nodes[i], b = state.nodes[i+1];
      var curLen = Math.sqrt(Math.pow(b.x-a.x,2)+Math.pow(b.y-a.y,2)) || xRange()/4 || 8;
      var bIsFixedTop = (i+1 === state.nodes.length-1);
      var aIsFixedBottom = (i === 0);

      if(!bIsFixedTop){
        b.y = safeYClamp(i+1, a.y);
        b.x = clamp(a.x+curLen, 0, xRange());
      } else if(!aIsFixedBottom){
        a.y = safeYClamp(i, b.y);
        a.x = clamp(b.x+curLen, 0, xRange());
      } else {
        b.x = clamp(a.x+curLen, 0, xRange());
      }
    });
    render();
  }

  function renderBandPanel(el){
    var n = state.selectedBands.length;
    var hasLine = state.selectedBands.some(function(i){ return state.types[i]==='line'; });
    var maxIdx = Math.max.apply(null, state.selectedBands);
    var targetNode = maxIdx+1;
    var showHeight = targetNode < state.nodes.length-1;

    var allLine = state.selectedBands.every(function(i){ return state.types[i]==='line'; });
    var allCurve = state.selectedBands.every(function(i){ return state.types[i]==='curve'; });

    var singleCurve = (n===1 && state.types[state.selectedBands[0]]==='curve');
    var isArmedNow = singleCurve && state.curvatureArmedBand === state.selectedBands[0];

    var html = '';
    if(singleCurve && !isArmedNow && !hasShownReshapeHint){
      hasShownReshapeHint = true;
      html += '<div class="nudge">Tip: '+(isCoarsePointer()?'press and hold':'double-click')+' this curve to bend it, or use Reshape Curve below.</div>';
    }
    if(isArmedNow){
      html += '<div class="nudge">Reshaping: drag the curve to bend it. Press Done when finished.</div>';
    }
    html += '<div class="field-row">';
    html += '<label>'+n+' segment'+(n>1?'s':'')+'</label>';
    if(singleCurve){
      html += '<button id="reshapeBtn" class="'+(isArmedNow?'active':'')+'">'+(isArmedNow?'Done':'Reshape Curve')+'</button>';
    }
    if(!allLine){ html += '<button id="makeLine">Make Line</button>'; }
    if(!allCurve){ html += '<button id="makeCurve">Make Curve</button>'; }
    if(hasLine){
      html += '<button id="vertBtn">Snap Vertical</button>';
      html += '<button id="horizBtn">Snap Horizontal</button>';
    }
    html += '</div>';

    if(showHeight){
      html += '<div class="field-row">';
      html += '<label>Snap height</label>';
      html += '<input type="number" step="1" id="bandHeightSnap" value="'+Math.round(state.nodes[targetNode].y)+'">';
      html += '<span class="unit">mm</span>';
      html += '</div>';
    }

    el.innerHTML = html;

    var mlBtn = ROOT.getElementById('makeLine');
    if(mlBtn){ mlBtn.addEventListener('click', function(){
      pushHistory();
      state.selectedBands.forEach(function(i){ state.types[i] = 'line'; });
      render();
    }); }
    var mcBtn = ROOT.getElementById('makeCurve');
    if(mcBtn){ mcBtn.addEventListener('click', function(){
      pushHistory();
      state.selectedBands.forEach(function(i){ state.types[i] = 'curve'; });
      render();
    }); }

    if(hasLine){
      ROOT.getElementById('vertBtn').addEventListener('click', applyVertical);
      ROOT.getElementById('horizBtn').addEventListener('click', applyHorizontal);
    }

    if(showHeight){
      var bhInput = ROOT.getElementById('bandHeightSnap');
      bhInput.addEventListener('focus', function(){ pushHistory(); });
      bhInput.addEventListener('input', function(evt){
        var v = parseFloat(evt.target.value);
        if(isNaN(v)) return;
        var result = applyBandHeightSnap(v);
        if(result!==null){ evt.target.value = result; }
      });
    }
  }

  // ---------- shrinkage controls ----------
  function refreshShrinkUI(){
    var lab = ROOT.getElementById('heightLabel');
    // Name the scale, not just "Height" - it is the same word for two very
    // different numbers otherwise.
    if(lab) lab.textContent = (shrinkPct === 0 ? 'Height'
      : (firedMode() ? 'Fired Height' : 'Rib Height')) + ':';

    var der = ROOT.getElementById('heightDerived');
    if(der){
      der.textContent = shrinkPct === 0 ? ''
        : firedMode() ? '= ' + Math.round(state.totalHeight) + 'mm rib'
                      : '\u2248 ' + Math.round(toFired(state.totalHeight)) + 'mm fired';
    }
    var seg = ROOT.getElementById('sizeMode');
    if(seg){
      Array.prototype.forEach.call(seg.querySelectorAll('button'), function(b){
        b.classList.toggle('active', b.getAttribute('data-mode') === sizeMode);
        b.disabled = (shrinkPct === 0 && b.getAttribute('data-mode') === 'fired');
      });
    }
    var rn = ROOT.getElementById('roundingNote');
    if(rn) rn.style.display = shrinkPct > 0 ? '' : 'none';

    // Only worth saying once the rib has actually grown, and not again until
    // the shrinkage changes or they leave it alone.
    var bn = ROOT.getElementById('backingNote');
    if(bn){
      var grown = firedMode() && state.totalHeight > Math.round(toFired(state.totalHeight)) + 6;
      if(grown && !backingNoted){
        bn.style.display = '';
        // Don't assume they want it wider - a different shrinkage figure can
        // make the rib smaller as easily as larger. Say the size changed and
        // let them decide.
        bn.innerHTML = 'Your rib is now <strong>' + Math.round(state.totalHeight) + 'mm</strong> tall, '
          + 'for a ' + Math.round(toFired(state.totalHeight)) + 'mm finished pot. '
          + 'It may be worth checking the backing width still suits it.';
      } else { bn.style.display = 'none'; }
    }
  }

  var shrinkInput = ROOT.getElementById('shrinkInput');
  if(shrinkInput){
    shrinkInput.addEventListener('input', function(){
      var v = parseFloat(this.value);
      shrinkPct = isFinite(v) ? clamp(v, 0, MAX_SHRINK) : 0;
      if(shrinkPct === 0 && sizeMode === 'fired'){ sizeMode = 'rib'; }
      backingNoted = false;          // a new figure means a new rib to think about
      if(activeTier){ applyTier(activeTier, true); }
      refreshShrinkUI();
      render();
    });
  }

  var sizeModeRow = ROOT.getElementById('sizeMode');
  if(sizeModeRow){
    sizeModeRow.addEventListener('click', function(evt){
      var b = evt.target.closest('button[data-mode]');
      if(!b || b.disabled) return;
      sizeMode = b.getAttribute('data-mode');
      if(activeTier){ applyTier(activeTier, true); }   // relabel, same design
      refreshShrinkUI();
      render();
    });
  }

  var heightSelect = ROOT.getElementById('heightInput');
  // Options are populated by applyTier() based on the selected variant.
  heightSelect.addEventListener('change', function(evt){
    pushHistory();
    // In fired mode the option's label is the pot; the rib it needs is on the
    // option itself. The design is always the rib.
    var opt = evt.target.selectedOptions ? evt.target.selectedOptions[0] : null;
    var ribAttr = opt && opt.getAttribute('data-rib');
    var newH = ribAttr ? parseFloat(ribAttr) : parseFloat(evt.target.value);
    var factor = newH/state.totalHeight;
    state.nodes.forEach(function(n){ n.y = n.y*factor; n.x = n.x*factor; });
    scaleHoleProfileOnly(factor);
    state.totalHeight = newH;
    backingNoted = false;
    // Pin endpoints exactly: repeated float scaling otherwise drifts the top
    // node a fraction off the stated height and the error accumulates.
    if(state.nodes.length >= 2){
      state.nodes[0].y = 0;
      state.nodes[state.nodes.length-1].y = state.totalHeight;
    }
    updatePriceDisplay();
    render();
  });

  ROOT.getElementById('resetLink').addEventListener('click', function(evt){
    evt.preventDefault();
    if(locked) return;
    pushHistory();
    // Stand the starting profile off the spine so both sides of the rib are
    // visible from the outset. At x=0 there is no pot side to see at all,
    // which is most of why the drawing reads as ambiguous.
    //
    // Proportional to the height, NOT a flat 10mm. A fixed distance looks
    // right at the middle sizes but crowds the POT SIDE label on tall ribs -
    // at 185mm a 10mm profile sits 3px from it, and the label hides itself.
    // A tenth of the height gives the same visual gap at every size, and
    // still works out at about 10mm on a medium rib.
    var startX = xRange()*0.2;
    state.nodes = [{y:0,x:startX},{y:state.totalHeight,x:startX}];
    state.types = ['line'];
    state.bulges = [0];
    state.hole = null;
    state.backingMm = BACK_MIN;
    clearSelection();
    render();
  });

  ROOT.getElementById('undoLink').addEventListener('click', function(evt){
    evt.preventDefault();
    if(locked) return;
    undo();
  });

  function mirrorDesign(sourceIsTop){
    var mid = state.totalHeight/2;
    var midIdx = -1;
    for(var i=0;i<state.nodes.length;i++){
      if(Math.abs(state.nodes[i].y - mid) < 0.01){ midIdx = i; break; }
    }
    pushHistory();
    if(midIdx === -1){
      var bandIdx = findBandForY(mid);
      if(bandIdx === -1){ history.pop(); return; }
      var a = state.nodes[bandIdx], b = state.nodes[bandIdx+1];
      var t = (mid-a.y)/((b.y-a.y) || 1);
      var midX = a.x + (b.x-a.x)*t;
      var origType = state.types[bandIdx];
      var origBulge = state.bulges[bandIdx];
      state.nodes.splice(bandIdx+1, 0, {y:mid, x:midX});
      state.types.splice(bandIdx, 1, origType, origType);
      state.bulges.splice(bandIdx, 1, origBulge*0.5, origBulge*0.5);
      midIdx = bandIdx+1;
    }

    if(!sourceIsTop){
      var lowerNodes = state.nodes.slice(0, midIdx+1);
      var lowerTypes = state.types.slice(0, midIdx);
      var lowerBulges = state.bulges.slice(0, midIdx);

      var upperNodes = [];
      for(var k=lowerNodes.length-2; k>=0; k--){
        upperNodes.push({y: state.totalHeight - lowerNodes[k].y, x: lowerNodes[k].x});
      }
      var upperTypes = [];
      var upperBulges = [];
      for(var k2=lowerTypes.length-1; k2>=0; k2--){
        upperTypes.push(lowerTypes[k2]);
        upperBulges.push(lowerBulges[k2]);
      }

      state.nodes = lowerNodes.concat(upperNodes);
      state.types = lowerTypes.concat(upperTypes);
      state.bulges = lowerBulges.concat(upperBulges);
    } else {
      var upperNodes2 = state.nodes.slice(midIdx);
      var upperTypes2 = state.types.slice(midIdx);
      var upperBulges2 = state.bulges.slice(midIdx);

      var lowerNodes2 = [];
      for(var k3=1; k3<upperNodes2.length; k3++){
        lowerNodes2.push({y: state.totalHeight - upperNodes2[k3].y, x: upperNodes2[k3].x});
      }
      lowerNodes2.reverse();

      var lowerTypes2 = [];
      var lowerBulges2 = [];
      for(var k4=upperTypes2.length-1; k4>=0; k4--){
        lowerTypes2.push(upperTypes2[k4]);
        lowerBulges2.push(upperBulges2[k4]);
      }

      state.nodes = lowerNodes2.concat(upperNodes2);
      state.types = lowerTypes2.concat(upperTypes2);
      state.bulges = lowerBulges2.concat(upperBulges2);
    }

    clearSelection();
    render();
  }
  ROOT.getElementById('mirrorTopLink').addEventListener('click', function(evt){
    evt.preventDefault();
    if(locked) return;
    mirrorDesign(true);
  });
  ROOT.getElementById('mirrorBottomLink').addEventListener('click', function(evt){
    evt.preventDefault();
    if(locked) return;
    mirrorDesign(false);
  });

  document.addEventListener('keydown', function(evt){
    if(locked) return;
    var tag = (evt.target && evt.target.tagName || '').toLowerCase();
    if(tag==='input' || tag==='select' || tag==='textarea') return;
    var key = evt.key;
    if(['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].indexOf(key)===-1) return;
    var step = evt.shiftKey ? 5 : 1;

    if(state.selectedNodes.length===1){
      var idx = state.selectedNodes[0];
      var isEndpoint = (idx===0 || idx===state.nodes.length-1);
      if((key==='ArrowUp'||key==='ArrowDown') && isEndpoint) return;
      evt.preventDefault();
      pushHistory();
      var n = state.nodes[idx];
      if(key==='ArrowLeft'){ n.x = clamp(n.x-step, 0, xRange()); }
      else if(key==='ArrowRight'){ n.x = clamp(n.x+step, 0, xRange()); }
      else if(key==='ArrowUp'){ n.y = safeYClamp(idx, n.y+step); }
      else if(key==='ArrowDown'){ n.y = safeYClamp(idx, n.y-step); }
      render();
    } else if(state.selectedBands.length>0){
      var bands = state.selectedBands;
      var armedCurve = state.curvatureArmedBand!==null && bands.indexOf(state.curvatureArmedBand)!==-1;
      if((key==='ArrowUp'||key==='ArrowDown') && !armedCurve) return;
      evt.preventDefault();
      pushHistory();
      if(key==='ArrowUp' || key==='ArrowDown'){
        var deltaPx = (key==='ArrowUp'?1:-1) * step * 2;
        bands.forEach(function(bi){
          if(state.types[bi]==='curve'){ state.bulges[bi] = clamp(state.bulges[bi]+deltaPx, -maxBulgePx(), maxBulgePx()); }
        });
      } else {
        var deltaX = (key==='ArrowRight'?1:-1) * step;
        var nodeSet = {};
        bands.forEach(function(bi){ nodeSet[bi]=true; nodeSet[bi+1]=true; });
        Object.keys(nodeSet).forEach(function(s){
          var ni = parseInt(s,10);
          state.nodes[ni].x = clamp(state.nodes[ni].x+deltaX, 0, xRange());
        });
      }
      render();
    }
  });

  // Use the customer's own design name for the file, so a saved copy is
  // recognisable later rather than every file being called the same thing.
  function suggestedFilename(){
    var el = ROOT.getElementById('designName');
    var raw = el && el.value ? el.value.trim() : '';
    var base = raw
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
    if(!base){ base = 'rib-profile-' + Math.round(state.totalHeight) + 'mm'; }
    return base + '.json';
  }

  function saveBlobFallback(jsonStr, filename){
    var blob = new Blob([jsonStr], {type:'application/json'});
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename || 'rib-profile.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function downloadSketch(){
    return downloadSketchAsync();
  }

  function downloadSketchAsync(){
    var data = {
      totalHeight: state.totalHeight,
      nodes: state.nodes,
      types: state.types,
      bulges: state.bulges,
      backing: state.backingMm,
      hole: state.hole
    };
    // Save the shrinkage the design was drawn with, so reopening it shows the
    // same numbers. Without this a saved sketch had nothing to restore from
    // and always reopened as though no shrinkage had been set.
    if(shrinkPct > 0){
      data.shrinkage = Math.round(shrinkPct*10)/10;
      data.firedHeight = Math.round(toFired(state.totalHeight)*10)/10;
      data.sizeMode = sizeMode;
    }
    var jsonStr = JSON.stringify(data, null, 2);
    var fname = suggestedFilename();

    // showSaveFilePicker gives a real "Save As" dialog with an editable
    // filename, but only exists in a secure context (https). Fall back to a
    // plain download elsewhere, and if the picker throws for any reason
    // other than the user cancelling.
    if(window.isSecureContext && window.showSaveFilePicker){
      return window.showSaveFilePicker({
        suggestedName: fname,
        types: [{description:'Rib design', accept: {'application/json': ['.json']}}]
      }).then(function(handle){
        return handle.createWritable();
      }).then(function(w){
        return w.write(jsonStr).then(function(){ return w.close(); });
      }).catch(function(err){
        if(err && err.name === 'AbortError'){ return; }
        saveBlobFallback(jsonStr, fname);
      });
    }
    saveBlobFallback(jsonStr, fname);
    return Promise.resolve();
  }
  ROOT.getElementById('downloadBtn').addEventListener('click', downloadSketch);



  // Real-world-scaled SVG export: same curve math as the on-screen canvas
  // (smoothed tangents, per-segment height clamping), but expressed directly
  // in millimeters instead of screen pixels, with no interactive-canvas
  // artifacts (no axis, no grid, no fill) — just the profile outline.
  // Profile geometry in real millimetres, with y measured from the TOP of
  // the rib downwards (0 = top, totalHeight = bottom) to match SVG's axis.
  // Shared by the rib view, the hole clearance maths and the SVG export, so
  // all three describe exactly the same shape.
  function ribGeometry(backingMm){
    var back = (typeof backingMm === 'number') ? backingMm : state.backingMm;
    function mmPoint(i){
      var n = state.nodes[i];
      return {x: n.x, y: state.totalHeight - n.y};
    }
    function dir(p1,p2){
      var dx=p2.x-p1.x, dy=p2.y-p1.y, L=Math.sqrt(dx*dx+dy*dy)||1;
      return {x:dx/L, y:dy/L};
    }
    function bez(A,C1,C2,B,t){
      var mt=1-t;
      return {
        x: mt*mt*mt*A.x + 3*mt*mt*t*C1.x + 3*mt*t*t*C2.x + t*t*t*B.x,
        y: mt*mt*mt*A.y + 3*mt*mt*t*C1.y + 3*mt*t*t*C2.y + t*t*t*B.y
      };
    }
    function f(v){ return Math.round(v*100)/100; }

    // Same control-point maths as the editor canvas, expressed in mm.
    var P = [], mmBulges = [];
    for(var q=0;q<state.nodes.length;q++){ P.push(mmPoint(q)); }
    for(var q2=0;q2<state.bulges.length;q2++){ mmBulges.push(state.bulges[q2]/scale()); }

    var segs = [], samples = [], maxX = 0;
    for(var i=0;i<state.types.length;i++){
      var A = P[i], B = P[i+1];
      maxX = Math.max(maxX, A.x, B.x);
      if(state.types[i] !== 'curve'){
        segs.push({type:'line', A:A, B:B});
        for(var s=0;s<=20;s++){
          var t = s/20;
          samples.push({x: A.x + (B.x-A.x)*t, y: A.y + (B.y-A.y)*t});
        }
        continue;
      }
      var cp = controlPointsFor(P, state.types, mmBulges, i, 0, xRange());
      segs.push({type:'curve', A:A, B:B, C1:cp.C1, C2:cp.C2});
      for(var k=0;k<=30;k++){
        var p = bez(A,cp.C1,cp.C2,B,k/30);
        samples.push(p);
        if(p.x > maxX) maxX = p.x;
      }
    }

    var backX = maxX + back;
    var d = 'M'+f(segs[0].A.x)+','+f(segs[0].A.y);
    segs.forEach(function(sg){
      if(sg.type==='line'){ d += ' L'+f(sg.B.x)+','+f(sg.B.y); }
      else { d += ' C'+f(sg.C1.x)+','+f(sg.C1.y)+' '+f(sg.C2.x)+','+f(sg.C2.y)+' '+f(sg.B.x)+','+f(sg.B.y); }
    });
    // The two backing-side corners get a 3mm radius. The profile edge and
    // its corners are left exactly as drawn.
    var last = segs[segs.length-1].B, first = segs[0].A;
    var topY = Math.min(last.y, first.y), botY = Math.max(last.y, first.y);
    var endsAtTop = (last.y <= first.y);
    var r = CORNER_R;
    if((botY - topY) > r*2 && back > r){
      if(endsAtTop){
        d += ' L'+f(backX-r)+','+f(topY);
        d += ' A'+f(r)+','+f(r)+' 0 0 1 '+f(backX)+','+f(topY+r);
        d += ' L'+f(backX)+','+f(botY-r);
        d += ' A'+f(r)+','+f(r)+' 0 0 1 '+f(backX-r)+','+f(botY);
      } else {
        d += ' L'+f(backX-r)+','+f(botY);
        d += ' A'+f(r)+','+f(r)+' 0 0 0 '+f(backX)+','+f(botY-r);
        d += ' L'+f(backX)+','+f(topY+r);
        d += ' A'+f(r)+','+f(r)+' 0 0 0 '+f(backX-r)+','+f(topY);
      }
      d += ' Z';
    } else {
      d += ' L'+f(backX)+','+f(last.y)+' L'+f(backX)+','+f(first.y)+' Z';
    }

    return {d:d, maxX:maxX, backX:backX, backing:back, H:state.totalHeight, samples:samples};
  }

  // Horizontal position of the profile edge at a given height.
  function profileXAt(y, geo){
    var best = null, bestDy = Infinity, maxAtY = null;
    geo.samples.forEach(function(p){
      var dy = Math.abs(p.y - y);
      if(dy < 0.75){ maxAtY = (maxAtY===null) ? p.x : Math.max(maxAtY, p.x); }
      if(dy < bestDy){ bestDy = dy; best = p; }
    });
    if(maxAtY !== null) return maxAtY;
    return best ? best.x : 0;
  }

  // A hole is valid when its full circle plus the margin sits inside the rib.
  function holeFits(x, y, d, geo){
    var r = d/2 + HOLE_MARGIN;
    if(y - r < 0 || y + r > geo.H) return false;
    if(x + r > geo.backX) return false;
    if(x - r < profileXAt(y, geo)) return false;
    for(var i=0;i<geo.samples.length;i++){
      var dx = geo.samples[i].x - x, dy = geo.samples[i].y - y;
      if(dx*dx + dy*dy < r*r) return false;
    }
    return true;
  }

  // Nudge a requested position to the nearest one that fits, so dragging
  // feels like sliding against a wall rather than snapping back.
  function clampHole(x, y, d, geo){
    var r = d/2 + HOLE_MARGIN;
    y = clamp(y, r, geo.H - r);
    x = Math.min(x, geo.backX - r);
    if(holeFits(x, y, d, geo)) return {x:x, y:y};
    for(var push=0; push<=geo.backX; push+=0.5){
      if(holeFits(x + push, y, d, geo)) return {x:x+push, y:y};
    }
    for(var dy=0.5; dy<geo.H; dy+=0.5){
      for(var sgn=-1; sgn<=1; sgn+=2){
        var ty = clamp(y + sgn*dy, r, geo.H - r);
        for(var px=0; px<=geo.backX; px+=0.5){
          if(holeFits(x + px, ty, d, geo)) return {x:x+px, y:ty};
        }
      }
    }
    return null;
  }

  function largestHoleThatFits(x, y, geo){
    for(var d=HOLE_MAX_D; d>=HOLE_MIN_D; d-=0.5){
      if(clampHole(x, y, d, geo)) return d;
    }
    return 0;
  }

  // Kept for a possible export button. Delegates to ribGeometry so the
  // exported outline is always identical to what the customer saw.
  function generateProfileSVG(minBackMm){
    var geo = ribGeometry(typeof minBackMm === 'number' ? minBackMm : state.backingMm);
    var pad = 2;
    var w = geo.backX, h = geo.H;
    var inner = '<path d="'+geo.d+'" fill="none" stroke="#000000" stroke-width="0.2"/>';
    if(state.hole){
      inner += '\n<circle cx="'+state.hole.x+'" cy="'+state.hole.y+'" r="'+(state.hole.d/2)+
               '" fill="none" stroke="#000000" stroke-width="0.2"/>';
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" width="'+(w+pad*2)+'mm" height="'+(h+pad*2)+'mm" '+
      'viewBox="'+(-pad)+' '+(-pad)+' '+(w+pad*2)+' '+(h+pad*2)+'">\n'+inner+'\n</svg>';
  }

  ROOT.getElementById('uploadBtn').addEventListener('click', function(){
    ROOT.getElementById('uploadInput').click();
  });

  ROOT.getElementById('uploadInput').addEventListener('change', function(evt){
    var file = evt.target.files[0];
    evt.target.value = '';
    if(!file || locked) return;
    var reader = new FileReader();
    reader.onload = function(e){
      try{
        var data = JSON.parse(e.target.result);
        if(!Array.isArray(data.nodes) || !Array.isArray(data.types) || !Array.isArray(data.bulges) || typeof data.totalHeight !== 'number'){
          throw new Error('missing fields');
        }
        if(data.nodes.length !== data.types.length+1 || data.bulges.length !== data.types.length){
          throw new Error('array length mismatch');
        }
        pushHistory();
        var rawH = data.totalHeight;

        // Restore the shrinkage figure the design was drawn with, so a sketch
        // reopens showing the same numbers it was saved with.
        if(typeof data.shrinkage === 'number'){
          shrinkPct = clamp(data.shrinkage, 0, MAX_SHRINK);
          var si = ROOT.getElementById('shrinkInput');
          if(si) si.value = String(Math.round(shrinkPct*10)/10);
          // A file carrying a fired height was drawn in fired sizes, so open
          // it that way - reopening in rib sizes shows an unfamiliar number.
          sizeMode = (data.sizeMode === 'fired' || data.sizeMode === 'rib')
            ? data.sizeMode
            : ((typeof data.firedHeight === 'number' && shrinkPct > 0) ? 'fired' : 'rib');
        } else {
          shrinkPct = 0; sizeMode = 'rib';
          var si0 = ROOT.getElementById('shrinkInput');
          if(si0) si0.value = '0';
        }

        // Heights used to be multiples of five, so uploads were snapped to the
        // nearest step. Working in fired sizes produces ribs on any millimetre
        // - 153mm - and snapping those to 155 reloads the WRONG TOOL. Keep
        // whole millimetres as they are; only tidy genuine fractions.
        var snappedH = clamp(Math.round(rawH), MIN_H, MAX_H);
        var factor = rawH>0 ? snappedH/rawH : 1;
        var newNodes = data.nodes.map(function(n){ return {y:n.y*factor, x:n.x*factor}; });
        state.totalHeight = snappedH;
        state.nodes = newNodes;
        state.types = data.types.slice();
        state.bulges = data.bulges.slice();
        state.backingMm = (typeof data.backing === 'number')
          ? clamp(data.backing, BACK_MIN, BACK_MAX) : BACK_MIN;
        state.hole = null;
        if(data.hole && typeof data.hole.d === 'number'){
          state.hole = {
            x: data.hole.x * factor,
            y: data.hole.y * factor,
            d: clamp(data.hole.d, HOLE_MIN_D, HOLE_MAX_D)
          };
        }
        if(state.nodes.length >= 2){
          state.nodes[0].y = 0;
          state.nodes[state.nodes.length-1].y = state.totalHeight;
        }

        // The height dropdown only lists the current tier's heights, so an
        // uploaded design from another size needs the variant switched first,
        // otherwise the height can't be selected and price/variant go stale.
        var tier = heightToTierName(snappedH);
        if(tier){
          if(tier !== activeTier){ selectVariantTier(tier); }
          applyTier(tier, true, true);   // keep the height the file specifies
        }
        // Select by the RIB the option represents, not by its label. In fired
        // mode the option values are pot sizes, so setting the value to the
        // rib height matches nothing and the picker goes blank.
        (function(){
          var opts = heightSelect.options, best = -1, bestGap = Infinity;
          for(var oi=0; oi<opts.length; oi++){
            var rib = parseFloat(opts[oi].getAttribute('data-rib'));
            if(!isFinite(rib)) rib = parseFloat(opts[oi].value);
            var gap = Math.abs(rib - snappedH);
            if(gap < bestGap){ bestGap = gap; best = oi; }
          }
          if(best >= 0) heightSelect.selectedIndex = best;
        })();
        if(state.hole){
          var g = ribGeometry();
          var pos = clampHole(state.hole.x, state.hole.y, state.hole.d, g);
          if(pos){ state.hole.x = pos.x; state.hole.y = pos.y; }
          else { state.hole = null; }
        }
        updatePriceDisplay();
        clearSelection();
        render();
      }catch(err){
        alert('That file does not look like a valid rib design.');
      }
    };
    reader.readAsText(file);
  });

  // --- Shopify product form integration ---
  // The design travels as a hidden line item property on the theme's own
  // Add to Cart form, so the native buy button (and Shop Pay etc) works
  // normally and there's no second, competing add-to-cart path.
  // Short stable label so multiple custom designs are distinguishable in the
  // cart and on the order. Derived from the design itself, so the same shape
  // always produces the same code.
  function designLabel(){
    var el = ROOT.getElementById('designName');
    var v = el && el.value ? el.value.trim() : '';
    if(v){ return v.slice(0, 60); }
    return 'Custom profile';
  }

  function designPayload(){
    // Last line of defence. Profile edits made without revisiting the Rib &
    // Hole step could otherwise send a hole that sits outside the rib.
    reconcileHole();
    // Two decimals, not whole millimetres. Rounding to integers was fine for a
    // hand-drawn profile with a handful of widely spaced points, but a preset
    // carried over from the viewer can have nodes well under a millimetre
    // apart. Those collapse onto the same value, the segment between them
    // becomes zero-length, and the shape that reaches the workshop is not the
    // one the customer saw. Bulges too: a whole-millimetre bulge is a visibly
    // different curve from the one drawn.
    var r2 = function(v){ return Math.round(v*100)/100; };
    var bands = [];
    for(var i=0;i<state.types.length;i++){
      var band = {
        fromY: r2(state.nodes[i].y), toY: r2(state.nodes[i+1].y),
        fromX: r2(state.nodes[i].x), toX: r2(state.nodes[i+1].x),
        type: state.types[i]
      };
      if(state.types[i]==='curve'){ band.bulge = r2(state.bulges[i]); }
      // A zero-length segment describes nothing and can trip up anything
      // downstream that divides by its length.
      if(band.fromY !== band.toY || band.fromX !== band.toX){ bands.push(band); }
    }
    var out = {totalHeight: Math.round(state.totalHeight), units:'mm', bands: bands,
               backing: Math.round(state.backingMm)};
    // Metadata, not geometry: the design is always the actual rib. Recording
    // the figure they worked to, and the pot they were aiming for, means an
    // order says what they wanted without anyone recomputing it.
    if(shrinkPct > 0){
      out.shrinkage = Math.round(shrinkPct*10)/10;
      out.firedHeight = Math.round(toFired(state.totalHeight)*10)/10;
    }
    if(state.hole){
      out.hole = {
        x: Math.round(state.hole.x*10)/10,
        y: Math.round(state.hole.y*10)/10,
        d: Math.round(state.hole.d*10)/10
      };
    }
    return out;
  }

  function ensureHiddenInputs(){
    var f = productForm();
    if(!f) return null;
    var profile = f.querySelector('input[name="properties[_rib_profile]"]');
    if(!profile){
      profile = document.createElement('input');
      profile.type = 'hidden';
      profile.name = 'properties[_rib_profile]';
      f.appendChild(profile);
    }
    var height = f.querySelector('input[name="properties[Height]"]');
    if(!height){
      height = document.createElement('input');
      height.type = 'hidden';
      height.name = 'properties[Height]';
      f.appendChild(height);
    }
    return {profile: profile, height: height};
  }

  function syncDesignToForm(){
    var inputs = ensureHiddenInputs();
    if(!inputs) return;
    inputs.profile.value = JSON.stringify(designPayload());
    inputs.height.value = state.totalHeight + 'mm';
    var f2 = productForm();
    if(f2){
      var lbl = f2.querySelector('input[name="properties[Design]"]');
      if(!lbl){
        lbl = document.createElement('input');
        lbl.type = 'hidden';
        lbl.name = 'properties[Design]';
        f2.appendChild(lbl);
      }
      lbl.value = designLabel();
    }
  }

  function showDesignWarning(msg){
    var el = ROOT.getElementById('designWarning');
    if(!el) return;
    el.textContent = msg || '';
    el.style.display = msg ? 'block' : 'none';
  }

  var saveOffered = false;
  var pendingAddBtn = null;

  // A real modal over the page, rather than a panel inside the tool, so it
  // can't be missed or scrolled past. Styles are inline so the theme's CSS
  // can't override them, and it lives on <body> so nothing can clip it.
  var modalEl = null;

  function buildModal(){
    if(modalEl) return modalEl;
    var overlay = document.createElement('div');
    overlay.setAttribute('role','dialog');
    overlay.setAttribute('aria-modal','true');
    overlay.setAttribute('aria-label','Save your design');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483600;background:rgba(23,23,23,0.55);' +
      'display:flex;align-items:center;justify-content:center;padding:20px;';

    var card = document.createElement('div');
    card.style.cssText = 'background:#FFFFFF;border-radius:12px;padding:22px;max-width:380px;width:100%;' +
      'box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;' +
      'color:#171717;text-align:left;';

    var h = document.createElement('div');
    h.textContent = 'Save a copy of your design?';
    h.style.cssText = 'font-size:16px;font-weight:500;margin:0 0 8px;color:#171717;';

    var p = document.createElement('div');
    p.textContent = 'Keep the file if you might want to reorder or tweak this shape later. Your design is added to the cart either way.';
    p.style.cssText = 'font-size:13.5px;line-height:1.55;margin:0 0 18px;color:#473728;';

    var row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;';

    var yes = document.createElement('button');
    yes.type = 'button';
    yes.textContent = 'Yes, Save a Copy';
    yes.style.cssText = 'flex:1;font-family:inherit;font-size:13.5px;padding:11px 12px;border-radius:8px;' +
      'border:1px solid #473728;background:#473728;color:#FFFFFF;cursor:pointer;';

    var no = document.createElement('button');
    no.type = 'button';
    no.textContent = 'No, Add to Cart';
    no.style.cssText = 'flex:1;font-family:inherit;font-size:13.5px;padding:11px 12px;border-radius:8px;' +
      'border:1px solid #D2C1B4;background:#FFFFFF;color:#473728;cursor:pointer;';

    yes.addEventListener('click', function(){
      var r = downloadSketch();
      if(r && r.then){ r.then(proceedWithAdd, proceedWithAdd); }
      else { proceedWithAdd(); }
    });
    no.addEventListener('click', proceedWithAdd);

    overlay.addEventListener('keydown', function(e){
      if(e.key === 'Escape'){ proceedWithAdd(); }
    });

    row.appendChild(yes); row.appendChild(no);
    card.appendChild(h); card.appendChild(p); card.appendChild(row);
    overlay.appendChild(card);
    modalEl = overlay;
    modalEl.__yes = yes;
    return overlay;
  }

  function showSavePrompt(btn){
    pendingAddBtn = btn;
    var m = buildModal();
    if(!m.parentNode){ document.body.appendChild(m); }
    m.style.display = 'flex';
    try { m.__yes.focus(); } catch(e){}
  }

  function hideSavePrompt(){
    if(modalEl){ modalEl.style.display = 'none'; }
  }

  // Continue the add-to-cart the customer originally clicked.
  function proceedWithAdd(){
    saveOffered = true;
    hideSavePrompt();
    var btn = pendingAddBtn;
    pendingAddBtn = null;
    addedToCart = true;
    syncDesignToForm();
    if(btn){
      btn.click();
    } else {
      var f = productForm();
      if(f){
        if(f.requestSubmit){ f.requestSubmit(); } else { f.submit(); }
      }
    }
  }

  function markAdded(){ addedToCart = true; }

  function blockIfBlank(evt){
    if(!isDesignBlank()) { showDesignWarning(''); syncDesignToForm(); return true; }
    evt.preventDefault();
    evt.stopPropagation();
    showDesignWarning('Please draw your rib profile before adding to cart.');
    try { ROOT.getElementById('canvas').scrollIntoView({behavior:'smooth', block:'center'}); } catch(e){}
    return false;
  }

  // Block submission when nothing has been drawn, and offer the design
  // download once the theme's add-to-cart request completes.
  // Concept renders parts of the product form outside the <form> element
  // (the size picker does this too), so binding to the form alone can miss
  // the buy button entirely. Listen on the document in capture phase and
  // identify the add button by shape rather than by ancestry.
  var ADD_SELECTORS = [
    'button[name="add"]', '[name="add"]', '.product-form__submit',
    '[data-add-to-cart]', '.add-to-cart', '.btn--add-to-cart',
    'button[type="submit"]', 'input[type="submit"]'
  ].join(', ');

  function isAddButton(el){
    if(!el || !el.closest) return null;
    var btn = el.closest(ADD_SELECTORS);
    if(!btn) return null;
    // Ignore submits belonging to unrelated forms (search, newsletter, login).
    var f = btn.closest('form');
    if(f){
      var action = f.getAttribute('action') || '';
      if(action.indexOf('/cart/add') === -1 && !f.querySelector('input[name="id"]')) return null;
    } else {
      var txt = (btn.textContent || '').toLowerCase();
      var nm = (btn.getAttribute('name') || '').toLowerCase();
      if(nm !== 'add' && txt.indexOf('cart') === -1 && txt.indexOf('buy') === -1) return null;
    }
    return btn;
  }

  function hookProductForm(){
    document.addEventListener('click', function(evt){
      var btn = isAddButton(evt.target);
      if(!btn) return;

      if(isDesignBlank()){
        evt.preventDefault();
        evt.stopPropagation();
        if(evt.stopImmediatePropagation) evt.stopImmediatePropagation();
        showDesignWarning('Please draw your rib profile before adding to cart.');
        try { ROOT.getElementById('canvas').scrollIntoView({behavior:'smooth', block:'center'}); } catch(e){}
        return;
      }
      showDesignWarning('');
      syncDesignToForm();

      if(!saveOffered){
        evt.preventDefault();
        evt.stopPropagation();
        if(evt.stopImmediatePropagation) evt.stopImmediatePropagation();
        showSavePrompt(btn);
      }
    }, true);

    var f = productForm();
    if(f){
      f.addEventListener('submit', function(evt){
        if(isDesignBlank()){
          evt.preventDefault();
          evt.stopPropagation();
          showDesignWarning('Please draw your rib profile before adding to cart.');
          return;
        }
        syncDesignToForm();
        if(!saveOffered){
          evt.preventDefault();
          evt.stopPropagation();
          showSavePrompt(null);
        }
      }, true);
    }

    // Themes build their cart request in JS rather than serialising the whole
    // form, so hidden inputs alone are not enough — the properties have to be
    // injected into the outgoing request itself.
    function injectProps(body){
      if(isDesignBlank()) return body;
      var props = {
        '_rib_profile': JSON.stringify(designPayload()),
        'Height': state.totalHeight + 'mm',
        'Design': designLabel()
      };
      var k;
      if(body instanceof FormData){
        for(k in props){ body.set('properties[' + k + ']', props[k]); }
        return body;
      }
      if(body instanceof URLSearchParams){
        for(k in props){ body.set('properties[' + k + ']', props[k]); }
        return body;
      }
      if(typeof body === 'string'){
        try {
          var obj = JSON.parse(body);
          if(obj && typeof obj === 'object'){
            if(Array.isArray(obj.items)){
              obj.items.forEach(function(it){
                it.properties = Object.assign({}, it.properties || {}, props);
              });
            } else {
              obj.properties = Object.assign({}, obj.properties || {}, props);
            }
            return JSON.stringify(obj);
          }
        } catch(e){
          var usp = new URLSearchParams(body);
          for(k in props){ usp.set('properties[' + k + ']', props[k]); }
          return usp.toString();
        }
      }
      return body;
    }

    var realFetch = window.fetch;
    if(realFetch){
      window.fetch = function(url, opts){
        var isAdd = String(url && url.url ? url.url : url).indexOf('/cart/add') !== -1;
        if(isAdd && opts && opts.body){
          try { opts = Object.assign({}, opts, {body: injectProps(opts.body)}); } catch(e){}
        }
        return realFetch.call(this, url, opts).then(function(res){
          if(isAdd && res.ok && !isDesignBlank()){ markAdded(); }
          return res;
        });
      };
    }

    var XHR = window.XMLHttpRequest;
    if(XHR && XHR.prototype && XHR.prototype.send){
      var realOpen = XHR.prototype.open;
      var realSend = XHR.prototype.send;
      XHR.prototype.open = function(method, url){
        this.__ribIsAdd = String(url).indexOf('/cart/add') !== -1;
        return realOpen.apply(this, arguments);
      };
      XHR.prototype.send = function(body){
        if(this.__ribIsAdd && body){
          try { body = injectProps(body); } catch(e){}
          if(!isDesignBlank()){
            var self = this;
            this.addEventListener('load', function(){
              if(self.status >= 200 && self.status < 300){ markAdded(); }
            });
          }
        }
        return realSend.call(this, body);
      };
    }
  }

  window.addEventListener('beforeunload', function(e){
    if(!locked && !addedToCart && !isDesignBlank()){
      e.preventDefault();
      e.returnValue = '';
    }
  });

  // Read-only introspection hook for automated testing. Never called by the
  // UI itself, never mutates anything — safe to leave in production.
  // A preset product can hand its design over via sessionStorage (rather
  // than the URL, which cannot carry designs of several thousand
  // characters safely). Applied once, then cleared so a refresh does not
  // silently re-apply it over later edits.
  function applyPresetHandoff(){
    var raw;
    try { raw = sessionStorage.getItem('ceramind_rib_handoff'); } catch(e){ return false; }
    if(!raw) return false;
    try { sessionStorage.removeItem('ceramind_rib_handoff'); } catch(e){}
    var h;
    try { h = JSON.parse(raw); } catch(e){ return false; }
    var d = h && h.design;
    if(!d || !Array.isArray(d.nodes) || !Array.isArray(d.types)) return false;

    // Work out the height FIRST, then let it decide the tier. Trusting the
    // size name meant that if it did not match exactly - missing, empty, or
    // the wrong case - the tier was never set, the design stayed at its own
    // height, and the variant kept whatever the page had defaulted to. That
    // is how a 115mm rib came to be sold as a Micro. The height is the
    // authoritative value; the name is only a hint.
    var height = clamp(Math.round((h.height||d.totalHeight)/H_STEP)*H_STEP, MIN_H, MAX_H);
    var tier = heightToTierName(height);
    if(h.size && tierHeights(h.size) && tierHeights(h.size).indexOf(height) !== -1){
      tier = h.size;                       // the name agrees, so honour it
    }
    if(tier){ selectVariantTier(tier); applyTier(tier, true, true); }
    var f = (d.totalHeight > 0) ? height/d.totalHeight : 1;
    state.totalHeight = height;
    state.nodes = d.nodes.map(function(n){ return {y:n.y*f, x:n.x*f}; });
    state.types = d.types.slice();
    state.bulges = (d.bulges||[]).map(function(b){ return b*f; });
    if(state.nodes.length >= 2){
      state.nodes[0].y = 0;
      state.nodes[state.nodes.length-1].y = state.totalHeight;
    }
    state.backingMm = (typeof d.backing === 'number')
      ? clamp(d.backing, BACK_MIN, BACK_MAX) : BACK_MIN;
    state.hole = null;              // presets carry no hole; they add their own
    // A preset is someone else's design at a fixed rib size. Applying a
    // shrinkage figure would silently resize it, so always open in rib mode.
    sizeMode = 'rib';
    heightSelect.value = String(height);

    // Carry the preset's name across as a starting design name, so a
    // customised preset arrives in the cart identifiable rather than blank.
    // Only prefill an empty field - never overwrite something typed.
    if(h.from){
      var nameEl = ROOT.getElementById('designName');
      if(nameEl && !nameEl.value.trim()){
        nameEl.value = String(h.from).slice(0, 60);
      }
    }

    // Last check: the variant on the form must describe the rib being sent.
    // Anything that leaves them disagreeing produces an order for the wrong
    // size, which is worse than any drawing fault.
    var finalTier = heightToTierName(state.totalHeight);
    if(finalTier && finalTier !== selectedTierFromPicker()){ selectVariantTier(finalTier); }

    clearSelection();
    return true;
  }

  (function initShopify(){
    var tier = selectedTierFromPicker();
    if(!tier){ var v = currentVariant(); tier = v ? v.tier : null; }
    if(tier){ applyTier(tier, true); }
    watchVariantPicker();
    hookProductForm();
    applyPresetHandoff();
    syncDesignToForm();
  })();
  updatePriceDisplay();

  // Diagnostic hook: reports what the tool can see of the theme's size picker.
  window.__ribDiag = function(){
    var sel = sizeSelectEl();
    return {
      sizeSelectFound: !!sel,
      sizeSelectName: sel ? sel.name : null,
      sizeSelectValue: sel ? sel.value : null,
      optionValues: sel ? Array.prototype.map.call(sel.options, function(o){return o.value;}) : null,
      detectedTier: selectedTierFromPicker(),
      activeTier: activeTier,
      toolHeight: state.totalHeight,
      variantsLoaded: (window.RIB_VARIANTS||[]).map(function(v){return v.tier+':'+v.id;}),
      addButtonsSeen: Array.prototype.map.call(document.querySelectorAll(ADD_SELECTORS), function(b){
        return (b.tagName.toLowerCase()+'['+(b.getAttribute('name')||b.className||'')+']').slice(0,40);
      }),
      saveOffered: saveOffered,
      designBlank: isDesignBlank()
    };
  };

  // Geometry introspection, used for testing the hole placement maths.
  window.__ribGeoDebug = function(){
    var g = ribGeometry();
    var cx = spacerCentre(g);
    var y = g.H/2;
    return {
      H: g.H, maxX: g.maxX, backX: g.backX, backing: g.backing,
      spacerCentre: cx,
      defaultFits: holeFits(cx, y, HOLE_DEFAULT_D, g),
      clamped: clampHole(cx, y, HOLE_DEFAULT_D, g),
      profileXAtMid: profileXAt(y, g),
      sampleCount: g.samples.length
    };
  };

  window.__curveDebug = function(){
    var P=[]; for(var k=0;k<state.nodes.length;k++){ P.push(svgNode(k)); }
    var out=[];
    for(var i=0;i<state.types.length;i++){
      if(state.types[i]!=='curve'){ out.push({i:i,type:'line'}); continue; }
      var A=P[i],B=P[i+1];
      var dx=B.x-A.x,dy=B.y-A.y,L=Math.sqrt(dx*dx+dy*dy)||1,h=L/3;
      var th=bulgeAngle(P,state.types,state.bulges,i);
      var tA=sharedTangent(P,state.types,state.bulges,i)||rotVec(chordDir(P,i),th);
      var tB=sharedTangent(P,state.types,state.bulges,i+1)||rotVec(chordDir(P,i),-th);
      var yLo=Math.min(A.y,B.y),yHi=Math.max(A.y,B.y);
      var la=rotVec(chordDir(P,i),th), lb=rotVec(chordDir(P,i),-th);
      var hA=pickHandle(A,tA,la,h,AXIS_X,Infinity,yLo,yHi);
      var hB=pickHandle(B,{x:-tB.x,y:-tB.y},{x:-lb.x,y:-lb.y},h,AXIS_X,Infinity,yLo,yHi);
      var fA=hA.f, fB=hB.f;
      out.push({i:i,segLen:+L.toFixed(1),theta:+(th*180/Math.PI).toFixed(1),
                fA:+fA.toFixed(3),fB:+fB.toFixed(3),
                tA:[+tA.x.toFixed(3),+tA.y.toFixed(3)],tB:[+tB.x.toFixed(3),+tB.y.toFixed(3)]});
    }
    return out;
  };

  window.__getDesignState = function(){
    return JSON.parse(JSON.stringify({
      totalHeight: state.totalHeight,
      nodes: state.nodes,
      types: state.types,
      bulges: state.bulges,
      locked: locked,
      view: state.view,
      backingMm: state.backingMm,
      hole: state.hole
    }));
  };

  render();

})();
